import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Printer, Check } from 'lucide-react';
import { COLORS, formatCurrency, formatPercent, sumAccounts, getAccountById, getChangeColor } from '../lib/utils';
import { qboAPI } from '../lib/api';
import api from '../lib/api';
import {
  buildBalanceSheet,
  buildIncomeStatement,
  buildCashFlow,
  buildT12,
  prepareCfForDisplay,
  assertCfFootsForRender,
  assertBsFootsForRender,
  variancePct as varPct,
  defaultPeriodLabels,
  ENGAGEMENT_REPORTS,
  getStatementTitle,
  getFrameworkPhrase,
  getFooterFrameworkPhrase,
  getCompiledFooterFrameworkPhrase,
  getEffectiveFramework,
  isInterimPeriod,
  bsNetIncomeAnchor,
} from '../lib/reports';

const SMJ_LOGO_URL = "https://customer-assets.emergentagent.com/job_funds-portal/artifacts/xi0cc8ut_Clean%20Logo%20with%20Words.png";

// Print CSS — shared between handlePrint() (browser preview) and the
// headless server-side PDF render path (/print-preview route consumed by
// Playwright from /api/pdf/generate). DO NOT INLINE OR DUPLICATE.
// The .page-landscape rotation hack is retained for the browser preview
// path; the server PDF path uses .pdf-server class on <body> to disable
// rotation and switch T12 to a native landscape canvas (page.pdf({landscape:true})).
export const PRINT_CSS = `
@page {
  size: letter;
  margin: 0.4in 0.5in;
}
/* Named @page used by the T12 wrapper in popup-print mode (Print →
   Save as PDF). Browsers that support CSS Paged Media named pages
   (Chrome, Safari, modern Firefox) emit the T12 section on a true
   landscape sheet so the saved PDF opens with the content reading
   right-side-up — no device rotation needed by the client. */
@page t12-landscape {
  size: letter landscape;
  margin: 0.4in 0.5in;
}
/* V19 — Self-hosted Inter font, served from /fonts/ in the React
   public/ dir. No CDN dependency. font-display:block forces the
   browser to wait for the font file before rendering text (Playwright
   captures the PDF as soon as data-render-ready flips, so we cannot
   afford a fallback-then-swap flash that would race the snapshot). */
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-display: block;
  src: url('/fonts/Inter-400.woff2') format('woff2');
}
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 500;
  font-display: block;
  src: url('/fonts/Inter-500.woff2') format('woff2');
}
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-display: block;
  src: url('/fonts/Inter-600.woff2') format('woff2');
}
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 700;
  font-display: block;
  src: url('/fonts/Inter-700.woff2') format('woff2');
}
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 9pt;
  line-height: 1.4;
  color: #1A1D21;
  font-variant-numeric: tabular-nums;
  -webkit-font-feature-settings: "tnum" 1, "lnum" 1;
  font-feature-settings: "tnum" 1, "lnum" 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.page {
  page-break-after: always;
  min-height: 10in;
  display: flex;
  flex-direction: column;
  position: relative;
}
.page:last-child { page-break-after: avoid; }

.logo { display: block; max-width: 180px; }
.logo-large { display: block; max-width: 340px; margin: 0 auto; }

.header {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid #0085CA;
  text-align: center;
}
.header .company { font-size: 14pt; font-weight: 600; margin: 0; }
.header .period { font-size: 9pt; color: #6B7280; margin-top: 2px; }

.statement-title {
  font-size: 13pt;
  font-weight: 600;
  margin-bottom: 2px;
  text-align: center;
  color: #1A1D21;
}
.statement-period { font-size: 9pt; color: #6B7280; margin-bottom: 10px; text-align: center; }

.statement-content { flex: 1; }

/* T12 rotated-canvas font sizing (browser preview path / popup-print).
   Critical: cells must be visually distinct — values rendering wider
   than their column boundary visually merge with neighbors ("$264,2569,178"
   reading instead of "$264,256  $9,178"). The combination of font size +
   colgroup widths + nowrap + visible overflow caused this. The fix here
   is to:
     (1) shrink the table-cell font to 5.5pt so the widest expected
         monthly value ("($235,304)" = 10 chars) renders narrower than
         the cell's reserved width;
     (2) use 'font-variant-numeric: tabular-nums' so all digits share a
         uniform advance width — predictable cell-width sizing instead
         of variable-width digit widths defeating the calculation;
     (3) keep overflow:visible so no value is silently truncated, but
         the smaller font + tabular-nums means cells fit comfortably
         and won't overflow each other in practice.
   Server-PDF path overrides below (.pdf-server .ls-inner) use the same
   strategy at 6pt since the native landscape page is wider (~10in vs
   the 9.5in rotated canvas). */
/* V24 (2026-06-19) — T12 canonical typography, single source of truth
   for BOTH the browser-print (popup) path and the headless Export PDF
   (.pdf-server) path. Rebuilt to the June-11 reference the firm signed
   off on: ~8pt readable body, full account names (2-line wrap, NEVER
   truncate), 12 month columns + a T12 TOTAL column set off with a left
   rule. Bold subtotal/total rows drop to 7pt so the widest negative
   value ("$(133,411)") clears its column without colliding. Verified in
   a Playwright landscape harness against the reference PDF. */
.ls-inner table {
  font-size: 8pt;
  table-layout: fixed;
  font-variant-numeric: tabular-nums;
}
.ls-inner th {
  font-size: 7.5pt;
  padding: 3px 4px;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.ls-inner td {
  font-size: 8pt;
  padding: 1.6px 4px;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
/* Account label (first col): left-aligned, WRAPS to 2 lines when long
   (e.g. "Accounting Services - Collected"). Never ellipsize/truncate —
   truncation was the reverted format the firm rejected. */
.ls-inner td:first-child {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  font-variant-numeric: normal;
  padding-right: 8px;
}
/* T12 TOTAL column (last col): set off from the month columns with a
   vertical rule, matching the reference. */
.ls-inner td:last-child,
.ls-inner th:last-child {
  border-left: 1px solid #1A1D21;
}

table { width: 100%; border-collapse: collapse; }
th {
  text-align: left;
  font-size: 8.5pt;
  font-weight: 700;
  color: #1A1D21;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 5px 0 5px 0;
  border-bottom: 1px solid #1A1D21;
}
th:not(:first-child) { text-align: right; }
/* V19 — Body td. Default: light-gray bottom rule on every td so account
   rows have hairline separators between them (matching V_19 reference).
   Section-header / subtotal / total / grand-total rows opt out via
   their own border declarations below. */
td {
  padding: 2.5px 0;
  font-size: 8.5pt;
  border-bottom: 1px solid #DDDDDD;
}
td:not(:first-child) {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1, "lnum" 1;
}
/* V18 (2026-06-10) — column-gap guard for portrait statements.
   Without a horizontal gap between adjacent right-aligned numeric
   columns, a wide AMOUNT value (e.g. $2,289,589) butts directly into
   the next column's %REV value (e.g. 100.0%) on bold subtotal /
   grand-total rows where letter-spacing is tightest. Enforcing a
   minimum left-padding on every non-first column adds a guaranteed
   gutter gap; tabular-nums (already applied) keeps digit columns
   aligned. Scope: portrait .statement-content tables only — the
   T12 landscape .ls-inner tables retain their own tight padding
   (set on lines 155-168). */
.statement-content table td:not(:first-child),
.statement-content table th:not(:first-child) {
  padding-left: 18px;
}
/* V19 — Sub-section header (Current Assets, Cash & Equivalents,
   Property & Equipment …). Bold black with extra space above; no rule
   above and no rule below — it's a label, not a row. */
.section-header td {
  font-weight: 700;
  font-size: 9pt;
  color: #1A1D21;
  padding-top: 8px;
  padding-bottom: 2px;
  border-bottom: none;
}
.indent td:first-child { padding-left: 12px; }
/* V19 — Subtotal row (Total Current Assets, Total Cash & Equivalents,
   Total Members' Equity …). Bold with a thin black hairline above
   marking the sum. No bottom separator (this is the end of the
   sub-section group; the next sub-section's section-header gives the
   visual break). */
.subtotal td {
  font-weight: 700;
  border-top: 1px solid #1A1D21;
  border-bottom: none;
  padding-top: 3px;
  padding-bottom: 4px;
}
/* V19 — Section total (TOTAL ASSETS, TOTAL LIABILITIES). Same family
   as subtotal but with a thicker top rule signalling "end of major
   section". Bold uppercase content is set in the markup. */
.total td {
  font-weight: 700;
  border-top: 2px solid #1A1D21;
  border-bottom: none;
  padding-top: 4px;
  padding-bottom: 4px;
}
/* V19 — Grand total row (TOTAL LIABILITIES AND MEMBERS' EQUITY,
   NET INCOME). Thicker top rule + subtle blue brand tint background
   to set off the bottom-line number. The 2-pt double-border bottom of
   V18 is removed — V_19 reference uses a single thick top rule only. */
.grand-total td {
  font-weight: 700;
  /* V19 — single rule above, double rule below; monochrome. */
  border-top: 1px solid #1A1D21;
  border-bottom: 3px double #1A1D21;
  padding: 5px 0;
  background: transparent;
  text-decoration: none;
}

/* V19 (2026-06-10) — PDF render paths render comparison-column
   variances in MONOCHROME. On-screen dashboard preview retains the
   red/green coloring; PDF Export and Print drop it. Inline style on
   the Change/% Change cells uses inline 'color' green/red — we
   override with !important scoped to the print/PDF body classes
   only. */
body.mockup-print .py-comparison td[style*="color"],
body.pdf-server   .py-comparison td[style*="color"],
body.popup-print-window .py-comparison td[style*="color"] {
  color: #0F1115 !important;
}

/* V19 v4 (2026-06-12) — T12 landscape bold-row column collision fix
   via PROGRAMMATICALLY MEASURED column geometry. Replaces v2/v3
   estimation-based fixes that were rejected (3rd failure).
   Root cause: prior fixes used % widths sized to a guessed worst
   case. The actual widest bold value at 5.5pt Inter tabular-nums
   was canvas-measured at 50px (("($1,430,605)" or "($3,521,336)"
   bold) — wider than the 49px the 5.4%/9.5in geometry allocated.
   Fix:
     (1) Hard-code numeric col widths to 0.6354in (50px text + 5px
         td padding + 6px user-mandated gutter) via inline col styles.
         Inches make the width identical across 9.5in popup canvas
         and 10.1in pdf-server canvas; the label first col takes
         the remainder via table-layout: fixed.
     (2) Strip USD glyph from interior T12 body rows (keep only on
         first section row + bold subtotal/total rows) — narrows
         non-bold values further. See renderT12Income() stripping logic.
   Bold rows stay at 5.5pt; type hierarchy preserved. Applies to all
   three print scopes (mockup-print, popup-print-window, pdf-server)
   per the standing rule. */
body.mockup-print .ls-inner table,
body.popup-print-window .ls-inner table,
.pdf-server .ls-inner table {
  table-layout: fixed;
  width: 100%;
}
body.mockup-print .ls-inner td:not(:first-child),
body.mockup-print .ls-inner th:not(:first-child),
body.popup-print-window .ls-inner td:not(:first-child),
body.popup-print-window .ls-inner th:not(:first-child),
.pdf-server .ls-inner td:not(:first-child),
.pdf-server .ls-inner th:not(:first-child) {
  /* V24 — month/total numeric cells. Padding matches the harness the
     column widths were verified against. */
  padding: 1.6px 4px;
}
body.mockup-print .ls-inner tbody tr.grand-total td,
body.mockup-print .ls-inner tbody tr.subtotal td,
body.popup-print-window .ls-inner tbody tr.grand-total td,
body.popup-print-window .ls-inner tbody tr.subtotal td,
.pdf-server .ls-inner tbody tr.grand-total td,
.pdf-server .ls-inner tbody tr.subtotal td {
  /* V24 — bold rows drop to 7pt so the widest negative money value
     clears its column (was the "collision" the 5.5pt regression hid). */
  font-size: 7pt;
  letter-spacing: normal;
  padding: 1.6px 4px;
}
body.mockup-print .ls-inner tbody tr.margin-row td,
body.mockup-print .ls-inner tbody tr.t12-net-margin td,
body.popup-print-window .ls-inner tbody tr.margin-row td,
body.popup-print-window .ls-inner tbody tr.t12-net-margin td,
.pdf-server .ls-inner tbody tr.margin-row td,
.pdf-server .ls-inner tbody tr.t12-net-margin td {
  font-size: 7pt;
  letter-spacing: normal;
}

tr { page-break-inside: avoid; break-inside: avoid; }
.section-header { page-break-after: avoid; break-after: avoid; }
thead { display: table-header-group; }
tfoot { display: table-footer-group; }
.total, .grand-total, .subtotal {
  page-break-before: avoid;
  break-before: avoid;
}
.statement-content table { page-break-inside: auto; }
.statement-content { display: flex; flex-direction: column; min-height: 100%; }

.footer {
  text-align: center;
  font-size: 8pt;
  color: #6B7280;
  font-style: italic;
  padding-top: 8px;
  border-top: 1px solid #E6E8EB;
  margin-top: auto;
}

.cover-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  text-align: center;
  padding: 0.4in 0;
}
.cover-page .cover-spacer-top { flex: 0 0 1.2in; }
.cover-page .cover-title-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
.cover-page .cover-logo { max-width: 340px; margin: 0 auto 56px; display: block; }
.cover-page .cover-company {
  font-size: 22pt;
  font-weight: 600;
  color: #1A1D21;
  margin-bottom: 16px;
}
.cover-page .cover-title {
  font-size: 14pt;
  font-weight: 500;
  color: #4B5563;
  margin-bottom: 10px;
}
.cover-page .cover-period { font-size: 12pt; color: #6B7280; }
/* V18 Step 2.5 — Framework footer at bottom of cover page in italic
   muted gray. For framework='none' this is the AR-C 70.A6 verbatim
   disclosure; for other frameworks it mirrors the per-statement
   footer text so the package opens with the basis-of-accounting
   declaration the rest of the package will footer-stamp. */
.cover-page .cover-framework-footer {
  width: 100%;
  max-width: 6.2in;
  margin: 0 auto;
  padding: 0 0.4in;
  font-size: 10pt;
  font-style: italic;
  color: #4B5563;
  text-align: center;
  line-height: 1.45;
}

.compilation-content {
  display: flex;
  flex-direction: column;
  padding: 0 60px;
  height: 100%;
}
.compilation-logo { text-align: center; margin-bottom: 14px; }
.compilation-logo img { max-width: 200px; }
.compilation-content h2 {
  font-size: 14pt;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 14px;
  text-align: center;
  color: #1A1D21;
}
.compilation-content p {
  margin-bottom: 10px;
  text-align: left;
  font-size: 10pt;
  line-height: 1.4;
}
.compilation-content p + p { margin-top: 2px; }
.signature-block { margin-top: 14px; }
.firm-name { font-weight: 600; font-size: 11pt; }
.firm-location {
  color: #4B5563;
  font-size: 10pt;
  margin-top: 2px;
}

.two-col { display: flex; gap: 24px; }
.two-col > div { flex: 1; }

/* Browser-preview path: T12 rotated -90deg inside a portrait wrapper.
   Known limitation: Save-as-PDF from window.print() inherits this hack,
   so the saved PDF has a portrait T12 page with content rotated 90°
   (read sideways or rotate after save). For a true native-landscape T12
   in a PDF, use the dashboard "Export PDF" button (server-side path). */
/* Landscape T12 — CSS rotation hack (V17 behavior).
   The .page-landscape wrapper is a 7.5×9.5 portrait box; the
   .ls-inner inside is a 9.5×7.5 landscape canvas rotated -90°
   to fill that box. When the user does Save-as-PDF from the
   browser print dialog, Chrome's print engine picks up the
   rotated content and the resulting PDF page displays
   landscape-readable in PDF viewers.

   V19.1.3 — break-before: auto. The preceding .page element
   already carries "page-break-after: always", so .page-landscape
   inherits a fresh page from that. Adding a second "break-before:
   page" here compounds with the trailing break and some browsers
   emit a blank page in between (user-reported "blank page 8" on
   Export). Trust the preceding break. */
.page-landscape {
  break-before: auto;
  page-break-before: avoid;
  width: 7.5in;
  height: 9.5in;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
}
.page-landscape > .ls-inner {
  width: 9.5in;
  height: 7.5in;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
  transform-origin: center center;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

/* ── POPUP-PRINT NATIVE LANDSCAPE OVERRIDE ──────────────────────────
   When the user clicks Print and chooses "Save as PDF" in the OS
   dialog, the popup window has body.popup-print-window. Browsers
   that honor CSS Paged Media named pages (Chrome 85+, Safari 16+,
   Firefox 110+) will emit the T12 section on a TRUE landscape sheet
   via 'page: t12-landscape'. We disable the legacy -90deg rotation
   so the content isn't double-rotated, and let the table flow
   naturally across the landscape canvas (10in × 7.7in usable).

   On older browsers that don't honor named @page, the rotation
   fallback still applies because the override is scoped to
   .popup-print-window. */
body.popup-print-window .page-landscape {
  page: t12-landscape;
  width: 10in;
  height: 7.7in;
  margin: 0;
  position: static;
  overflow: visible;
  /* Force a page break BEFORE the T12 section so the previous
     statement doesn't get caught on the landscape sheet. */
  page-break-before: always;
  break-before: page;
  /* V18.1 (2026-06-10) — suppress the trailing page break after the
     T12 in the browser-print (popup) pipeline. Without this, the
     popup-print engine emits an empty trailing page after the T12
     because (a) .page-landscape is not a .page (so the
     '.page:last-child { page-break-after: avoid }' rule at line 101
     doesn't match the page immediately preceding the T12), and
     (b) .page-landscape had no page-break-after declaration of
     its own. The .pdf-server scope is unaffected — Playwright's
     page.pdf() stops at the last content block and never produced
     this trailing blank. Print-path subsystem only. */
  page-break-after: avoid;
  break-after: avoid;
}
body.popup-print-window .page-landscape > .ls-inner {
  width: 100%;
  height: 100%;
  position: static;
  top: auto;
  left: auto;
  transform: none;
  margin: 0;
  overflow: visible;
}

/* ── SERVER-PDF PATH OVERRIDES ───────────────────────────────────────
   When body has .pdf-server, the host page tells Playwright to call
   page.pdf({landscape:true}) for THIS render, so we want the T12
   .page-landscape wrapper to occupy the natural landscape page
   without any rotation. The page (per the calling @page rule)
   is 11in × 8.5in usable ~10.5in × 7.5in. We tune fonts/padding so
   the entire SMJ canonical IS row stream fits on ONE landscape page;
   if a future client has too many rows, the natural page-break
   will spill onto a 2nd page (acceptable, footer/title repeat via
   thead). */
.pdf-server .page-landscape {
  width: auto;
  height: auto;
  margin: 0;
  position: static;
  overflow: visible;
  break-before: auto;
  page-break-before: avoid;
}
.pdf-server .page-landscape > .ls-inner {
  width: 100%;
  /* V24 — allow dense T12s (e.g. LW Concierge, ~50 rows) to flow onto a
     2nd landscape sheet instead of clipping Net Income off the bottom.
     The old max-height:7.55in + overflow:hidden hard-clipped anything
     past one page. display:block (not flex) lets Chrome paginate the
     table; thead repeats via display:table-header-group. */
  position: static;
  transform: none;
  overflow: visible;
  display: block;
}
.pdf-server .ls-inner .statement-content { display: block; }
.pdf-server .ls-inner thead { display: table-header-group; }
.pdf-server .ls-inner tr { break-inside: avoid; page-break-inside: avoid; }
/* V24 — Export PDF (.pdf-server) T12 typography now MATCHES the base
   .ls-inner rules above (single source of truth). The old 5.6pt/ellipsis
   overrides that diverged Export from Print are removed. */
.pdf-server .ls-inner table { font-size: 8pt; font-variant-numeric: tabular-nums; }
.pdf-server .ls-inner th { font-size: 7.5pt; padding: 3px 4px; line-height: 1.1; font-variant-numeric: tabular-nums; }
.pdf-server .ls-inner td { font-size: 8pt; padding: 1.6px 4px; line-height: 1.15; white-space: nowrap; font-variant-numeric: tabular-nums; }
.pdf-server .ls-inner td:first-child { white-space: normal; overflow: visible; text-overflow: clip; padding-right: 8px; font-variant-numeric: normal; }
.pdf-server .page-landscape .ls-inner tbody tr.grand-total td { font-size: 7pt; }
.pdf-server .page-landscape .ls-inner tbody tr.subtotal td { font-size: 7pt; }
/* Section-header rows (REVENUE, OPERATING EXPENSES, etc.). */
.pdf-server .page-landscape .section-header td {
  padding-top: 4px !important;
  padding-bottom: 1px !important;
  font-size: 8pt !important;
}
/* Keep the small vertical padding from the base rule (no !important 0
   collapse — that crushed line height in the old cramped layout). */
.pdf-server .ls-inner tbody td { padding-top: 1.6px !important; padding-bottom: 1.6px !important; }
/* Compiled T12 uses identical typography to prepared. */
.pdf-server .page-landscape.t12-compiled .ls-inner td { padding: 1.6px 4px; line-height: 1.15; font-size: 8pt; }
.pdf-server .page-landscape.t12-compiled .ls-inner th { padding: 3px 4px; font-size: 7.5pt; line-height: 1.1; }
.pdf-server .page-landscape.t12-compiled .ls-inner table { font-size: 8pt; }
.pdf-server .page-landscape.t12-compiled .ls-inner tbody tr.grand-total td { font-size: 7pt; }
.pdf-server .page-landscape.t12-compiled .ls-inner tbody tr.subtotal td { font-size: 7pt; }
/* Tighten the masthead block above the T12 table so the row stream
   has the maximum possible vertical room on a single landscape page. */
.pdf-server .page-landscape .header { margin-bottom: 4px; padding-bottom: 3px; }
.pdf-server .page-landscape .header .company { font-size: 11pt; }
.pdf-server .page-landscape .statement-title { font-size: 10.5pt; margin-bottom: 1px; }
.pdf-server .page-landscape .statement-period { font-size: 8pt; margin-bottom: 3px; }
.pdf-server .page-landscape .footer { font-size: 7pt; padding-top: 4px; }

/* When section=landscape, the server PDF run only contains T12 — hide
   any other pages that might have been emitted. When section=portrait,
   hide the landscape page. The PrintPreviewPage already filters JSX,
   but defense-in-depth via CSS guards against drift. */
.pdf-server.section-portrait .page-landscape { display: none !important; }
.pdf-server.section-landscape .page { display: none !important; }

@media print {
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}

/* ════════════════════════════════════════════════════════════════════
   Presentation Pass — inlined mockup CSS (Day 3 cutover, 2026-06-01)
   ────────────────────────────────────────────────────────────────────
   Previously loaded from /print-mockup.css via PrintPreviewPage's
   conditional <link> tag. Inlined into PRINT_CSS so a single source
   of truth ships with the React bundle and the popup-print path picks
   it up automatically.

   The body.mockup-print selector is preserved verbatim from the
   original stylesheet; PrintModal adds the 'mockup-print' class to
   <body> on mount (and the headless PrintPreviewPage adds it for the
   server-PDF path). When PrintModal unmounts, the class is removed.
   ════════════════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════════════════
   SMJ Financial Statements — Presentation Pass Mockup CSS (Day 2)

   Day 2 revision. Day-1 review feedback addressed:
     • Section spacing tightened (~40% header→first row, ~30% inter-section)
     • Firm-brand blue rule restored on every statement page header
     • Cover firm-contact block gets breathing room + line-height
     • Compilation report breathing room INCREASED (was tightened in v1)
     • INCOME FROM OPERATIONS — 4-tier hierarchy (heavier than subtotals)
     • Digit alignment verified across $0 and small values
     • CF zero-suppression hooks unchanged
     • Body font Source Serif Pro 10pt (was 10.5pt) — page-count fit

   Target page count:
     SMJ FYTD prepared: cover + BS + IS + CF + BS_PY + IS_PY + T12 = 7
     Fayetteville compiled: cover + compilation report + BS + IS + CF + BS_PY + IS_PY + T12 = 8
   ════════════════════════════════════════════════════════════════════ */

/* ─── Typography baseline ─────────────────────────────────────────── */
body.mockup-print {
  font-family: "Source Serif Pro", Georgia, "Times New Roman", serif;
  font-size: 9.5pt;
  line-height: 1.24;
  color: #1A1D21;
}
/* Do NOT add padding to .page — production .page { min-height: 10in }
   already fills the printable area; adding padding here forces
   per-statement overflow onto a second printed page. The @page margin
   (0.4in vertical / 0.5in horizontal) is the only padding we need. */

/* ─── Cover page ──────────────────────────────────────────────────── */
body.mockup-print .cover-page {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  text-align: center;
  padding: 0.5in 0;
}
body.mockup-print .cover-page .cover-spacer-top { flex: 0 0 1.4in; }
body.mockup-print .cover-page .cover-title-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
body.mockup-print .cover-page .cover-logo {
  max-width: 1.7in;
  margin: 0 auto 0.85in;
  display: block;
}
body.mockup-print .cover-page .cover-company {
  font-family: "Source Serif Pro", Georgia, serif;
  font-size: 34pt;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0F1115;
  margin-bottom: 0.32in;
  line-height: 1.15;
}
body.mockup-print .cover-page .cover-title {
  font-size: 16pt;
  font-weight: 400;
  color: #2C3036;
  margin-bottom: 0.18in;
  letter-spacing: 0.005em;
}
body.mockup-print .cover-page .cover-period {
  font-size: 13pt;
  font-weight: 300;
  color: #4B5563;
  letter-spacing: 0.005em;
}

/* Bottom-of-page firm contact + full SSARS legend block.
   Day-2 — substantially more breathing room above + internal
   line-height + subtle blue brand separator. */
body.mockup-print .cover-page .cover-footer-block {
  width: 100%;
  max-width: 5.6in;
  margin: 0 auto;
  padding: 0.55in 0.4in 0;
  display: flex;
  flex-direction: column;
  gap: 0.26in;
  border-top: 1.5px solid #0085CA;
}
body.mockup-print .cover-page .cover-firm-contact {
  font-size: 9.5pt;
  color: #2C3036;
  letter-spacing: 0.01em;
  line-height: 1.85;
}
body.mockup-print .cover-page .cover-firm-contact .firm-name {
  font-weight: 600;
  font-size: 10.5pt;
  color: #1A1D21;
  margin-bottom: 0.08in;
  letter-spacing: 0.005em;
}
body.mockup-print .cover-page .cover-ssars-legend {
  font-size: 8.5pt;
  font-style: italic;
  color: #6B7280;
  line-height: 1.6;
  border-top: 0.5px solid #D1D5DB;
  padding-top: 0.16in;
  text-align: justify;
}
body.mockup-print .cover-page .cover-framework-footer { display: none; }

/* ─── Statement page common — DAY 2 restored firm-brand header ───── */
body.mockup-print .header {
  margin-bottom: 0.10in;
  padding-bottom: 0.05in;
  border-bottom: 2px solid #0085CA;     /* firm brand rule — restored */
  text-align: center;
}
body.mockup-print .header .company {
  font-family: "Source Serif Pro", Georgia, serif;
  font-size: 13pt;
  font-weight: 600;
  letter-spacing: 0.005em;
  color: #0F1115;
  margin: 0;
}
body.mockup-print .header .period {
  font-size: 8.5pt;
  font-style: italic;
  color: #6B7280;
  margin-top: 2px;
}
body.mockup-print .statement-title {
  font-size: 13pt;
  font-weight: 700;
  letter-spacing: 0.005em;
  color: #0F1115;
  margin-top: 0.10in;
  margin-bottom: 0.02in;
  text-align: center;
}
body.mockup-print .statement-period {
  font-size: 9pt;
  font-weight: 400;
  font-style: italic;
  color: #4B5563;
  margin-bottom: 0.10in;
  letter-spacing: 0.005em;
  text-align: center;
}

/* ─── Tables ─────────────────────────────────────────────────────── */
body.mockup-print table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
}
body.mockup-print thead th {
  font-size: 8.5pt;
  font-weight: 700;
  letter-spacing: 0.07em;
  color: #4B5563;
  text-transform: uppercase;
  padding-bottom: 0.05in;
  border-bottom: 0.75px solid #1F2937;
}
body.mockup-print thead th:first-child { text-align: left; }
body.mockup-print thead th:last-child  { text-align: right; }

body.mockup-print tbody td {
  font-size: 9pt;
  padding: 0.6pt 0;
  vertical-align: baseline;
  color: #1A1D21;
}
/* Day-2 — Tighten 5-column PY-comparison statements (Balance Sheet PY,
   Income Statement PY) so they fit on a single page. The extra columns
   ($ change, % change) reduce horizontal space per column; we offset
   that with tighter vertical rhythm and shorter horizontal padding. */
body.mockup-print .py-comparison tbody td {
  font-size: 8.5pt;
  padding: 0.4pt 0;
}
body.mockup-print .py-comparison .section-header td {
  padding-top: 0.06in;
  font-size: 7.5pt;
}
body.mockup-print .py-comparison thead th {
  font-size: 7.5pt;
}
/* Each numeric column on PY-comparison gets a touch less right-padding */
body.mockup-print .py-comparison tbody td:not(:first-child),
body.mockup-print .py-comparison thead th:not(:first-child) {
  padding-left: 0.08in;
}
body.mockup-print tbody td:first-child {
  text-align: left;
  padding-right: 0.18in;
}

/* ─── Digit alignment lock (item #5 of Day-2 corrections) ───────── */
/* The last column is the value column on BS/IS/CF/T12. Right-aligned
   on the closing edge, with the closing paren of negatives hanging
   into ~0.085in of trailing padding so positive and negative digits
   share the same column. Verified across $0, single-digit, and
   thousands-separated values. */
body.mockup-print tbody td:last-child {
  text-align: right;
  font-variant-numeric: tabular-nums lining-nums;
  font-feature-settings: "tnum" 1, "lnum" 1;
  white-space: nowrap;
  padding-right: 0.085in;
  font-family: "Source Serif Pro", Georgia, "Times New Roman", serif;
}
/* On comparison statements with multiple numeric columns ($, prior $,
   $ change, % change), apply tabular-nums to every numeric column,
   not just the last one. */
body.mockup-print tbody td:not(:first-child) {
  font-variant-numeric: tabular-nums lining-nums;
  font-feature-settings: "tnum" 1, "lnum" 1;
}

/* ─── Section spacing (item #1 of Day-2 corrections — tightened) ── */
body.mockup-print .section-header td {
  font-weight: 700;
  font-size: 8pt;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1F2937;
  padding-top: 0.08in;    /* was 0.13in; further tightened for fit */
  padding-bottom: 0.01in;
}
body.mockup-print thead + tbody .section-header:first-child td,
body.mockup-print tbody > tr.section-header:first-child td {
  padding-top: 0.03in;
}

/* ─── Rule convention (item #7) — subtotals, conclusions, totals ── */

/* Tier 2 — Subtotals (Total Revenue, Total Operating Expenses)
   Semibold, single thin rule above value. */
body.mockup-print tbody tr.subtotal td {
  font-weight: 600;
  padding-top: 0.025in;
  padding-bottom: 0.025in;
  border-top: 0.5px solid #1F2937;
  background: transparent !important;
}

/* Tier 3 — Section conclusions (GROSS PROFIT, INCOME FROM OPERATIONS).
   Heavier than subtotals (semibold + slight letter-spacing). Visually
   distinct from the Total Operating Expenses subtotal above it. */
body.mockup-print tbody tr.is-income-from-ops td,
body.mockup-print tbody tr.is-gross-profit td {
  font-weight: 700;
  font-size: 9.5pt;
  letter-spacing: 0.01em;
  padding-top: 0.035in;
  padding-bottom: 0.035in;
  border-top: 0.75px solid #1F2937;
  background: transparent !important;
}

/* Tier 4 — Grand totals (NET INCOME, TOTAL ASSETS, ENDING CASH).
   Bold + single rule above + double rule below. */
body.mockup-print tbody tr.grand-total td,
body.mockup-print tbody tr.total td,
body.mockup-print tbody tr.bs-total td,
body.mockup-print tbody tr.is-net-income td,
body.mockup-print tbody tr.cf-ending-cash td,
body.mockup-print tbody tr.cf-net-change td {
  /* V19 (2026-06-10) — grand-total rule treatment per V_19 reference:
       • single rule above, double rule below
       • continuous horizontal rules spanning the full row (border-collapse already applied)
       • NO vertical borders, NO background tint, NO underlined text
       • monochrome — color comes from font weight + rules only */
  font-weight: 700;
  font-size: 9.5pt;
  padding-top: 0.035in;
  padding-bottom: 0.04in;
  border-top: 1px solid #0F1115;
  border-bottom: 3px double #0F1115;
  background: transparent !important;
  text-decoration: none !important;
  color: #0F1115 !important;
}

/* T12 landscape exception — keep the Net Income grand-total row at
   the same font size as the rest of the table; otherwise the wider
   default 9.5pt parenthesized negatives like ($235,304) overflow into
   the adjacent monthly column and visually run-on against the next
   value. Inherits boldness + rule treatment from the rule above. */
body.mockup-print .ls-inner tbody tr.grand-total td {
  font-size: 5.5pt;
  padding-top: 1.5px;
  padding-bottom: 1.5px;
}
.pdf-server .ls-inner tbody tr.grand-total td {
  font-size: 7.5pt;
  padding-top: 2px;
  padding-bottom: 2px;
}

/* Indent (sub-account rows under group headers) */
body.mockup-print tbody tr.indent td:first-child { padding-left: 0.12in; }

/* ─── % column on PY statements & T12 ────────────────────────────── */
body.mockup-print .col-pct {
  padding-left: 0.2in !important;
  color: #4B5563;
}

/* ─── Per-statement footer (item #4) — single-line, italic, light ── */
body.mockup-print .footer {
  margin-top: 0.14in;
  text-align: center;
  font-size: 8pt;
  font-style: italic;
  color: #9CA3AF;
  letter-spacing: 0.01em;
  border-top: 0.5px solid #E5E7EB;
  padding-top: 0.05in;
  line-height: 1.4;
}

/* ─── Cash Flow zero-suppression visual cue ──────────────────────── */
body.mockup-print tr.cf-zero-suppressed { display: none; }

/* ─── Compilation Report (item #3 of Day-2 corrections — LOOSEN) ── */
/* Day-1 mockup tightened the paragraphs and shrank the logo, which
   review reported as cramped vs. current production. Restore breathing
   room: bigger logo, more inter-paragraph space, taller line-height,
   keep title centered. */
body.mockup-print .compilation-content {
  padding: 0 0.6in;
  display: flex;
  flex-direction: column;
  /* height:100% was previously set so the flex column filled the page.
     That made compilation-content nominally 10in tall — taller than the
     printable region in some configurations — and Chrome silently
     dropped the page-break-inside:avoid rule because the element could
     not fit on a single page. Removing it lets compilation-content size
     to its content; the avoid rule on .signature-block (below) keeps the
     3-line firm signature atomic. */
  page-break-inside: avoid;
  break-inside: avoid;
}
body.mockup-print .compilation-logo {
  text-align: center;
  margin-bottom: 0.22in;
}
body.mockup-print .compilation-logo img {
  max-width: 2.2in;
}
body.mockup-print .compilation-content h2 {
  font-size: 14pt;
  font-weight: 700;
  margin: 0.04in 0 0.22in;
  text-align: center;
  letter-spacing: 0.005em;
  color: #0F1115;
}
body.mockup-print .compilation-content p {
  margin-bottom: 0.12in;
  text-align: left;
  font-size: 10pt;
  line-height: 1.5;
  color: #1A1D21;
  /* Avoid widow/orphan lines in compilation paragraphs — keeps a
     paragraph's last 2 lines from being orphaned at the top of the
     next page (or its first 2 lines from being stranded at the
     bottom of the previous one). */
  orphans: 3;
  widows: 3;
}
body.mockup-print .compilation-content p + p {
  margin-top: 0.04in;
}
/* ── POPUP-PRINT NATIVE LANDSCAPE — readable font sizing ───────────
   Native landscape canvas (10in usable) has more horizontal room than
   the legacy 9.5in rotated canvas, so we bump the T12 cell font from
   5.5pt to 6.5pt for readability while still fitting all 13 columns
   with whitespace gutters. tabular-nums is inherited from .ls-inner
   table above. */
body.popup-print-window .ls-inner table { font-size: 6.5pt; }
body.popup-print-window .ls-inner th { font-size: 6pt; padding: 2.5px 3px; }
body.popup-print-window .ls-inner td { font-size: 6.5pt; padding: 1.5px 3px; }
body.popup-print-window .ls-inner tbody tr.grand-total td { font-size: 6.5pt; }

body.mockup-print .signature-block {
  margin-top: 0.18in;
  line-height: 1.5;
  /* Keep the 3-line typed-firm signature atomic so the report date
     never orphans onto a new page alone. */
  page-break-inside: avoid;
  break-inside: avoid;
}

/* ─── Day-2.5 — Balance Sheet density + page-break protection ───── */

/* BS uses two sequential tables (Assets, then Liabilities + Equity).
   Production renders them as two '<table>' blocks one after another.
   On clients with dense Members' Equity (e.g., Las Trojas Fayetteville
   with 12+ LTC sub-accounts), the second table can spill onto a
   second page. Day-2.5 — tighten BS-specific spacing by ~15% under
   SMJ's spacing floor, and guard the L+E table + grand total against
   being orphaned from their section context. */
body.mockup-print .balance-sheet-table tbody td {
  font-size: 8.5pt;
  padding: 0.45pt 0;
}
body.mockup-print .balance-sheet-table .section-header td {
  padding-top: 0.055in;
  padding-bottom: 0.005in;
  font-size: 7.5pt;
}
/* Subtotals + grand totals on BS — tighter padding than IS/CF subtotals */
body.mockup-print .balance-sheet-table tbody tr.subtotal td {
  padding-top: 0.018in;
  padding-bottom: 0.018in;
}
body.mockup-print .balance-sheet-table tbody tr.total td,
body.mockup-print .balance-sheet-table tbody tr.grand-total td {
  padding-top: 0.025in;
  padding-bottom: 0.012in;
}

/* ─── Major section-header (BS only — LIABILITIES AND MEMBERS' EQUITY)
       ─────────────────────────────────────────────────────────────────
   The BS is now rendered as ONE continuous '<table>' so Assets and
   Liabilities + Equity share the same column rhythm. The transition
   between asset side and liability side is marked with a single thin
   rule above the row, plus a touch of extra breathing room. Same row
   structure as a regular .section-header (label in description column,
   empty value cells) so the dollar columns above and below align
   exactly. */
body.mockup-print .balance-sheet-table tbody tr.section-header.major-section-header td,
body.mockup-print .py-comparison.bs-py-table tbody tr.section-header.major-section-header td {
  border-top: 0.75px solid #1F2937;
  padding-top: 0.12in;       /* slightly more breathing room than sub-section headers */
  padding-bottom: 0.025in;
}

/* Suppress THEAD page-repeat on the unified BS tables.
   Default 'display: table-header-group' makes the column header row
   (ASSETS | AMOUNT) repeat at the top of every page when the table
   spans multiple pages. With the new single-continuous-table BS, that
   would leak "ASSETS / AMOUNT" onto a page whose visible body is
   the Liabilities + Equity continuation — which is misleading. Forcing
   the thead into the row-group flow means it renders once at the top
   of page 1 and never repeats. */
body.mockup-print .balance-sheet-table thead,
body.mockup-print .py-comparison.bs-py-table thead {
  display: table-row-group;
}

/* Page-break protection — applies to BOTH the standalone Balance Sheet
   table (.balance-sheet-table) AND the BS PY comparison table
   (.py-comparison rendered from renderBalanceSheetPY).

   The BS is now a single continuous table. For SMJ-sized engagements,
   the table fits one page. For Fayetteville (12+ LTC sub-accounts on
   the equity side), the table MUST be allowed to break — but only
   between major blocks so we don't orphan a grand-total or subtotal.
   The preferred break point is BEFORE the .major-section-header row
   (asset side concluding, liability side starting). */
body.mockup-print .balance-sheet-table tbody tr.section-header.major-section-header,
body.mockup-print .py-comparison.bs-py-table tbody tr.section-header.major-section-header {
  /* Break is allowed (and welcome) before this row. */
  page-break-before: auto;
  break-before: auto;
  /* But the row itself must not be the last visible row on its page. */
  page-break-after: avoid;
  break-after: avoid;
}
body.mockup-print .balance-sheet-table tbody tr.section-header.major-section-header + tr,
body.mockup-print .py-comparison.bs-py-table tbody tr.section-header.major-section-header + tr {
  page-break-before: avoid;
  break-before: avoid;
}
body.mockup-print .balance-sheet-table tbody tr.total,
body.mockup-print .py-comparison.bs-py-table tbody tr.total,
body.mockup-print .py-comparison.bs-py-table tbody tr.grand-total {
  page-break-inside: avoid;
  break-inside: avoid;
  page-break-before: avoid;
  break-before: avoid;
}
/* Keep section headers glued to their first row */
body.mockup-print .balance-sheet-table tbody tr.section-header,
body.mockup-print .py-comparison.bs-py-table tbody tr.section-header {
  page-break-after: avoid;
  break-after: avoid;
}
body.mockup-print .balance-sheet-table tbody tr.section-header + tr,
body.mockup-print .py-comparison.bs-py-table tbody tr.section-header + tr {
  page-break-before: avoid;
  break-before: avoid;
}

/* ─── BS PY label column wrap fix (Day-2.5 item #3) ──────────────── */
/* "TOTAL LIABILITIES AND MEMBERS' EQUITY" wraps onto two lines when
   the description column is squeezed by 4 numeric companion columns.
   Widen the label column. */
body.mockup-print .py-comparison thead th:first-child,
body.mockup-print .py-comparison tbody td:first-child {
  min-width: 2.6in;
  padding-right: 0.12in;
}
body.mockup-print .py-comparison thead th:not(:first-child),
body.mockup-print .py-comparison tbody td:not(:first-child) {
  width: 0.95in;
  white-space: nowrap;
}
/* And make sure the grand-total LABEL on BS PY doesn't wrap */
body.mockup-print .py-comparison tbody tr.total td:first-child,
body.mockup-print .py-comparison tbody tr.grand-total td:first-child,
body.mockup-print .py-comparison tbody tr.bs-total td:first-child {
  white-space: nowrap;
  letter-spacing: -0.005em;   /* condense slightly to gain horizontal room */
}
body.mockup-print .page-landscape .ls-inner table { font-size: 7pt; }
body.mockup-print .page-landscape thead th { font-size: 6.5pt; padding-bottom: 0.025in; }
body.mockup-print .page-landscape tbody td { padding: 0.4pt 0; line-height: 1.12; }
body.mockup-print .page-landscape colgroup col.t12-name { width: 26%; }
body.mockup-print .page-landscape colgroup col.t12-month { width: 5.4%; }
body.mockup-print .page-landscape colgroup col.t12-total { width: 9%; }
body.mockup-print .page-landscape thead th {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05em;
  border-bottom: 0.75px solid #1F2937;
  color: #4B5563;
}
/* T12 Net Margin row — italic, lighter weight, single thin rule above
   separating it from Net Income. Day-2.5 — restored the rule and
   tightened the row's vertical footprint so it doesn't clip off the
   bottom of the landscape page. */
body.mockup-print .page-landscape tr.t12-net-margin td {
  font-style: italic;
  font-weight: 400;
  color: #4B5563;
  border-top: 0.5px solid #B0B4BA;
  padding-top: 1.2pt;
  padding-bottom: 1pt;
  font-size: 6.5pt;
}
body.mockup-print .page-landscape td:not(:first-child) {
  text-align: right;
  font-variant-numeric: tabular-nums lining-nums;
  font-feature-settings: "tnum" 1, "lnum" 1;
  white-space: nowrap;
  padding-right: 0.05in;
}
/* T12 page — minimize padding so Net Margin row + footer both fit
   within the 8.5in landscape page-area. */
body.mockup-print .page-landscape .ls-inner {
  padding: 0.22in 0.38in 0.16in !important;
}
/* Tighter footer on landscape T12 */
body.mockup-print .page-landscape .footer {
  margin-top: 0.05in;
  padding-top: 0.025in;
  font-size: 6.5pt;
  line-height: 1.3;
}
/* Header + statement-title on landscape — compact */
body.mockup-print .page-landscape .header { margin-bottom: 0.05in; padding-bottom: 0.03in; }
body.mockup-print .page-landscape .statement-title { font-size: 10.5pt; margin-top: 0.04in; margin-bottom: 0.01in; }
body.mockup-print .page-landscape .statement-period { font-size: 7.5pt; margin-bottom: 0.04in; }
/* Section header rows on T12 — tighter than the portrait IS rule */
body.mockup-print .page-landscape .section-header td {
  padding-top: 0.035in;
  padding-bottom: 0.01in;
  font-size: 6.5pt;
}

/* ─── No background shading on any total/conclusion ─────────────── */
body.mockup-print tr.bs-total td,
body.mockup-print tr.is-net-income td,
body.mockup-print tr.is-income-from-ops td,
body.mockup-print tr.is-gross-profit td,
body.mockup-print tr.cf-ending-cash td,
body.mockup-print tr.cf-net-change td,
body.mockup-print tr.grand-total td,
body.mockup-print tr.subtotal td {
  background: #fff !important;
}

/* ─── V24.1 (2026-06-19) — DETERMINISTIC compact density for the headless
   Export/Print PDF portrait statements (BS / IS / CF / PY comparisons).
   The 6.11 compact look was scoped only to body.mockup-print; this block
   binds the SAME density to the .pdf-server render path directly, so the
   exported PDF is compact regardless of class-cascade quirks or deploy
   drift. Scoped to .page (portrait) so the T12 (.page-landscape /
   .ls-inner) is untouched. Placed last in PRINT_CSS to win source order.
   Values mirror the verified body.mockup-print rules. ────────────────── */
.pdf-server .page .statement-content table tbody td {
  padding: 0.6pt 0 !important;
  line-height: 1.18 !important;
}
.pdf-server .page .balance-sheet-table tbody td,
.pdf-server .page .py-comparison tbody td {
  padding: 0.45pt 0 !important;
}
.pdf-server .page .statement-content table tbody tr.section-header td {
  padding-top: 0.05in !important;
  padding-bottom: 0.01in !important;
}
.pdf-server .page .statement-content table tbody tr.subtotal td {
  padding-top: 0.02in !important;
  padding-bottom: 0.02in !important;
}
/* V24.3a — the headless/print bodies must be pure white. The app shell's
   'body { background: var(--smj-bg) }' (#F0F5F8) used to be neutralised by
   the legacy @media print reset in index.css; that reset is now correctly
   scoped away from the PDF paths, so declare the white paper here instead. */
body.pdf-server,
body.mockup-print,
body.popup-print-window {
  background: #fff !important;
}
body.pdf-server .page,
body.pdf-server .page-landscape,
body.mockup-print .page,
body.mockup-print .page-landscape,
body.popup-print-window .page,
body.popup-print-window .page-landscape {
  background: #fff !important;
}

/* V24.3 (2026-06-20) — RESTORE THE TOTAL RULES.
   With border-collapse, the light hairline border-bottom of the account
   row directly above a subtotal/total competed with that row's own dark
   border-top; on a width tie the neighbour's gray line won, so the
   accounting rule above every "Total ..." line disappeared. Drop the
   preceding row's bottom hairline so the total's own rule always draws. */
tr:has(+ tr.subtotal) td,
tr:has(+ tr.total) td,
tr:has(+ tr.grand-total) td,
tr:has(+ tr.bs-total) td,
tr:has(+ tr.is-net-income) td,
tr:has(+ tr.is-gross-profit) td,
tr:has(+ tr.is-income-from-ops) td,
tr:has(+ tr.cf-ending-cash) td,
tr:has(+ tr.cf-net-change) td,
tr:has(+ tr.t12-net-margin) td {
  border-bottom: none !important;
}

.pdf-server .page .statement-content table tbody tr.total td,
.pdf-server .page .statement-content table tbody tr.grand-total td,
.pdf-server .page .statement-content table tbody tr.bs-total td,
.pdf-server .page .statement-content table tbody tr.is-net-income td,
.pdf-server .page .statement-content table tbody tr.cf-ending-cash td {
  padding-top: 0.025in !important;
  padding-bottom: 0.012in !important;
}

`;

const PERIOD_OPTIONS = [
  { id: 'prior_month', label: 'Prior Month', getRange: () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: firstDay, end: lastDay, label: firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) };
  }},
  { id: 'ytd', label: 'Year to Date', getRange: () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), 0, 1);
    return { start: firstDay, end: now, label: `January 1 - ${now.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}, ${now.getFullYear()}` };
  }},
  { id: 'last_quarter', label: 'Last Fiscal Quarter', getRange: () => {
    // Returns the last COMPLETED fiscal quarter (calendar-year quarters by default).
    // Example: Run on May 27, 2026 → Q1 2026 (Jan 1 – Mar 31, 2026).
    const now = new Date();
    const currentQ = Math.floor(now.getMonth() / 3); // 0,1,2,3
    let lastQYear = now.getFullYear();
    let lastQ = currentQ - 1;
    if (lastQ < 0) { lastQ = 3; lastQYear -= 1; }
    const start = new Date(lastQYear, lastQ * 3, 1);
    const end = new Date(lastQYear, lastQ * 3 + 3, 0);
    const qName = ['Q1', 'Q2', 'Q3', 'Q4'][lastQ];
    return { start, end, label: `${qName} ${lastQYear} (Quarter ended ${end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})` };
  }},
  { id: 'last_year', label: 'Last Fiscal Year', getRange: () => {
    const now = new Date();
    const year = now.getFullYear() - 1;
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return { start, end, label: `Year Ended December 31, ${year}` };
  }},
  { id: 'this_year', label: 'This Fiscal Year', getRange: () => {
    const now = new Date();
    return { start: new Date(now.getFullYear(), 0, 1), end: new Date(now.getFullYear(), 11, 31), label: `Year Ended December 31, ${now.getFullYear()}` };
  }},
  { id: 'custom', label: 'Custom Range', getRange: () => null },
];

// Statement options for Full Package and Custom
const STATEMENT_OPTIONS = [
  { id: 'bs', label: 'Balance Sheet', description: 'Assets, Liabilities & Equity' },
  { id: 'pl', label: 'Statement of Income', description: 'Revenue, Expenses & Net Income' },
  { id: 'cf', label: 'Statement of Cash Flows', description: 'Operating, Investing & Financing' },
  { id: 'bs_py', label: 'Balance Sheet - PY Comparison', description: 'With Prior Year Comparison' },
  { id: 'pl_py', label: 'Statement of Income - PY Comparison', description: 'With Prior Year Comparison' },
  { id: 't12', label: 'T12 Statement of Income', description: 'Trailing 12 Months' },
];

const PACKAGE_OPTIONS = [
  { id: 'current', label: 'Current Statement Only', description: 'Print only the currently selected statement' },
  { id: 'package', label: 'Full Financial Package', description: 'BS, P&L, Cash Flow, PY Comparisons, T12' },
  { id: 'custom', label: 'Custom Package', description: 'Select which statements to include' },
];

const REPORT_TYPES = [
  { id: 'prepared', label: 'Prepared Financial Statements', footer: 'No CPA assurance is given; no notes disclosed.' },
  { id: 'compiled', label: 'Compiled Financial Statements', footer: 'See Accountant\'s Compilation Report.' },
];

// Get equity labels based on entity type
const getEquityLabels = (entityType) => {
  switch(entityType) {
    case 's_corp':
    case 'c_corp':
      return { equity: "Stockholders' Equity", stock: 'Common Stock', retained: 'Retained Earnings' };
    case 'sole_prop':
      return { equity: "Owner's Equity", stock: "Owner's Capital", retained: "Owner's Draws" };
    case 'nonprofit':
      return { equity: 'Net Assets', stock: 'Net Assets Without Donor Restrictions',
               retained: 'Change in Net Assets' };
    case 'smllc':
    case 'multi_llc':
    case 'llc':
    default:
      return { equity: "Members' Equity", stock: "Members' Capital", retained: "Members' Draws & Distributions" };
  }
};

// Format date properly
const formatPeriodDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export default function PrintModal({ 
  isOpen, 
  onClose, 
  accounts, 
  client, 
  currentStatement = 'pl',
  trendData,
  ttmData,
  mapping,
  qboMeta = null,
  userRole = 'staff',  // 'staff' or 'client' - clients get restricted options
  // ── V16 Pass D: headless render path used by /print-preview route.
  //    When headless=true: don't render modal chrome — render the
  //    statement package directly into the document. Used by Playwright
  //    subprocess invoked by /api/pdf/generate.
  //    section='portrait' → render only non-T12 pages.
  //    section='landscape' → render only T12 page.
  //    section='all' (default) → render every page (browser preview).
  //    package='current'|'package'|'custom' → overrides UI state for the
  //    headless render.
  //    forceReportType='prepared'|'compiled' → overrides UI state.
  headless = false,
  section = 'all',
  forcePackage = null,
  forceReportType: forceReportTypeProp = null,
  // V20 — Consolidated group rendering. When provided, the inline per-section
  // builders are bypassed and the merged statements are used directly. The
  // `accounts` and `mapping` props are ignored for math (kept for legacy
  // ancillary derivations). `consolidatedMembers` is the per-member list
  // for any future drill-downs (currently informational only).
  consolidatedReports = null,
  consolidatedMembers = null,
}) {
  // Day-3 cutover (2026-06-01): mockup is now the canonical print
  // style. The legacy `printStyle` and `forceShowZeroesCf` props are
  // gone; CF zero suppression is driven by the in-modal `showZeroLines`
  // toggle alone (default OFF = suppressed). The `mockup-print` body
  // class is applied on PrintModal mount so the inlined CSS (below in
  // PRINT_CSS) attaches.
  useEffect(() => {
    document.body.classList.add('mockup-print');
    return () => { document.body.classList.remove('mockup-print'); };
  }, []);

  // Day-2 label normalizer (item #10). Applied at the leaf-render point.
  // Three transforms:
  //   • 'Work In Process' (any case) → 'Work in Process' (sentence case)
  //   • 'AR and ' / 'A/R and ' or trailing 'AR Adjustment' → 'A/R'
  //   • ' - ' between words → ' — ' (em dash, with non-breaking on either side)
  // The first two are CPA-standard abbreviations; the em-dash is the
  // typographic upgrade flagged by the design pass.
  const normalizeLeafLabel = (label) => {
    if (typeof label !== 'string') return label;
    let s = label;
    // 'Work In Process' → 'Work in Process' (any sentence position)
    s = s.replace(/\bWork In Process\b/g, 'Work in Process');
    // 'AR and ' → 'A/R and ' / 'AR ' (start or post-space) → 'A/R '
    s = s.replace(/(^|\W)AR(\s+and\b)/g, '$1A/R$2');
    s = s.replace(/(^|\W)AR(\s+Adjustment\b)/g, '$1A/R$2');
    // ' - ' (with surrounding spaces) → ' — ' (em dash)
    s = s.replace(/ - /g, ' \u2014 ');
    return s;
  };
  const [packageOption, setPackageOption] = useState('current');
  // Day-2 presentation pass — CF zero-line suppression toggle. Default
  // OFF (zero rows hidden). Staff can flip it on for completeness
  // verification before publishing.
  const [showZeroLines, setShowZeroLines] = useState(false);
  // Clients can only print "prepared" statements (no CPA assurance)
  const [reportType, setReportType] = useState('prepared');
  const [periodId, setPeriodId] = useState('prior_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  // V20.1 — When staff changes the period selector, refetch QBO data for
  // that period instead of relying on the snapshot the parent passed in.
  // Previously the modal kept showing the dashboard's prior-month data
  // even when the user picked "Last Fiscal Year" / "Last Fiscal Quarter".
  const [overrideAccounts, setOverrideAccounts] = useState(null);
  const [overrideQboMeta, setOverrideQboMeta] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState(null);
  // Custom package selections - all statements available
  const [selectedStatements, setSelectedStatements] = useState(['bs', 'pl', 'cf']);
  // V18 Step 2 — Unified server-side PDF path. The in-modal "Print"
  // button now calls /api/pdf/generate (same as the Export PDF button),
  // so both entry points read the persisted canonical mapping. While
  // the render runs (~9s), surface progress so staff knows the system
  // is alive (not frozen).
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfProgressLabel, setPdfProgressLabel] = useState('Generating PDF…');
  // V18 Step 2.5 — Unsaved-mapping-changes warning. Before kicking off
  // the server render, compare the dashboard's in-memory mapping to
  // the latest persisted mapping. If they differ, surface a modal so
  // the staff user can choose to save, continue anyway, or cancel.
  const [unsavedWarning, setUnsavedWarning] = useState(null); // {onContinue, onSave, onCancel} | null
  const printRef = useRef(null);

  // For clients, force "prepared" report type (no CPA info)
  const isClient = userRole === 'client';
  const effectiveReportType = forceReportTypeProp || (isClient ? 'prepared' : reportType);
  const effectivePackageOption = forcePackage || packageOption;

  // V18 Step 2 — Rotate the PDF progress label every 2.5s so the staff
  // user sees the system is making forward progress. The actual stage
  // boundaries are best-effort phrasing; the server doesn't currently
  // stream progress events back. If the server adds SSE in the future,
  // bind the label to those events instead.
  useEffect(() => {
    if (!pdfGenerating) return undefined;
    const phrases = [
      'Building statements…',
      'Rendering pages…',
      'Assembling PDF…',
    ];
    let idx = 0;
    setPdfProgressLabel(phrases[0]);
    const t = setInterval(() => {
      idx = (idx + 1) % phrases.length;
      setPdfProgressLabel(phrases[idx]);
    }, 2500);
    return () => clearInterval(t);
  }, [pdfGenerating]);

  // V19.1 — Pre-warm the server-side PDF cache when the modal opens.
  // This benefits the Export PDF button (server-side path). The Print
  // button is now client-side (V19.1) so it doesn't need a warm cache,
  // but staff users often Print then Export the same package, so the
  // pre-warm still earns its keep for the second click.
  //
  // CRITICAL: do NOT fire pre-warm in headless mode. The PrintModal
  // mounts inside the /print-preview route consumed by Playwright,
  // and a recursive /api/pdf/generate from inside that render would
  // (a) be rejected by the render-only JWT scope and surface a JS
  // error overlay onto the rendered PDF, (b) potentially spawn an
  // infinite render loop. Headless renders are not interactive
  // (the user can never click Print from inside them), so there is
  // nothing to pre-warm.
  useEffect(() => {
    if (headless) return;
    if (!isOpen || !client?.id) return;
    // V21.2 (2026-06-11) — Gate the pre-warm fire on period_end being
    // resolved. Without this, the modal opens, qboMeta is still
    // loading, and we fire /api/pdf/generate with period_end=null —
    // which the new 422 guard rejects every time. Silent failures
    // accumulate in the backend logs and the pre-warm produces no
    // cache benefit. Only fire once qboMeta.period_end is known.
    if (!qboMeta?.period_end) return;
    const ctrl = new AbortController();
    const baseUrl = window.location.origin;
    const effectiveReportType = client?.engagement_type === 'compilation' ? 'compiled' : 'prepared';
    axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/pdf/generate-async`,
      {
        client_id: client.id,
        package: 'package',
        report_type: effectiveReportType,
        period_end: qboMeta?.period_end || null,
        base_url: baseUrl,
        disposition: 'inline',
      },
      {
        headers: { Authorization: `Bearer ${localStorage.getItem('smj_token')}` },
        responseType: 'blob',
        signal: ctrl.signal,
        timeout: 310000,
      },
    ).catch(() => { /* silent — this is a cache pre-warm */ });
    return () => ctrl.abort();
  }, [headless, isOpen, client?.id, client?.engagement_type, qboMeta?.period_end]);

  // V20.1 — Refetch QBO accounts when the user picks a different period.
  // Skip in headless mode (the headless renderer already fetches with
  // the right period) and on consolidated groups (handled at the parent
  // level). Skip 'prior_month' since that's what the parent already
  // passed in (no-op) — keep the snapshot.
  useEffect(() => {
    if (headless || !isOpen) return;
    if (!client?.id || client.client_type === 'consolidated_group') return;
    if (consolidatedReports) return;
    if (periodId === 'prior_month') {
      setOverrideAccounts(null);
      setOverrideQboMeta(null);
      setPeriodError(null);
      return;
    }
    // Resolve the target period_end / period_start strings.
    let pStart = null;
    let pEnd = null;
    if (periodId === 'custom') {
      if (!customStart || !customEnd) return;
      pStart = customStart;
      pEnd = customEnd;
    } else {
      const opt = PERIOD_OPTIONS.find((p) => p.id === periodId);
      const range = opt?.getRange();
      if (!range?.start || !range?.end) return;
      pStart = range.start.toISOString().slice(0, 10);
      pEnd = range.end.toISOString().slice(0, 10);
    }
    if (!pEnd) return;
    const ctrl = new AbortController();
    setPeriodLoading(true);
    setPeriodError(null);
    qboAPI.getAccounts(client.id, pEnd, pStart)
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setOverrideAccounts(res.data.accounts || []);
        setOverrideQboMeta({
          reconciliation: res.data.reconciliation,
          completeness:   res.data.completeness,
          period_start:   res.data.period_start,
          period_end:     res.data.period_end,
          period_start_eve: res.data.period_start_eve,
          ni_at_period_start: res.data.ni_at_period_start,
          cash_at_period_start: res.data.cash_at_period_start,
        });
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        console.error('Period refetch failed:', err);
        setPeriodError(err?.response?.data?.detail || err.message || 'Failed to load period');
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setPeriodLoading(false);
      });
    return () => ctrl.abort();
  }, [periodId, customStart, customEnd, client?.id, client?.client_type, isOpen, headless, consolidatedReports]);

  if (!isOpen) return null;

  // V20.1 — `accounts` / `qboMeta` from props are the dashboard's prior-month
  // snapshot. When the user picks a different period in this modal, we
  // refetched and stored the response in overrideAccounts / overrideQboMeta.
  // Re-bind the local symbols so every downstream builder / renderer
  // transparently uses period-correct data without per-call wiring.
  if (overrideAccounts) accounts = overrideAccounts;
  if (overrideQboMeta) qboMeta = overrideQboMeta;

  const selectedPeriod = PERIOD_OPTIONS.find(p => p.id === periodId);
  const periodRange = periodId === 'custom' 
    ? { label: customStart && customEnd ? `${formatPeriodDate(customStart)} - ${formatPeriodDate(customEnd)}` : 'Select dates' }
    : selectedPeriod?.getRange();

  const selectedReportType = REPORT_TYPES.find(r => r.id === effectiveReportType);
  const equityLabels = getEquityLabels(client?.entity_type);

  // Group accounts by their type from QBO
  const getAccountsByType = (type) => {
    return accounts.filter(acc => acc.type === type).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  // Get sum of accounts by type
  const sumByType = (type) => {
    return getAccountsByType(type).reduce((sum, acc) => sum + (acc.balance || 0), 0);
  };

  // Check if we have valid mapping with actual accounts mapped
  const hasMappedAccounts = (section) => {
    if (!mapping || !mapping[section]) return false;
    return mapping[section].some(line => line.accounts && line.accounts.length > 0);
  };

  // Helper to sum all accounts in a mapping section (if mapping exists)
  const sumMappingSection = (section) => {
    if (!mapping || !mapping[section]) return 0;
    return mapping[section].reduce((total, line) => {
      return total + sumAccounts(accounts, line.accounts);
    }, 0);
  };

  // Get mapped line items with their values for a section
  const getMappedLines = (section) => {
    if (!mapping || !mapping[section]) return [];
    return mapping[section]
      .filter(line => line.accounts && line.accounts.length > 0)
      .map(line => ({
        label: line.label,
        accounts: line.accounts,
        balance: sumAccounts(accounts, line.accounts)
      }))
      // Hide lines with zero balance from final reports (standard CPA practice)
      .filter(line => Math.abs(line.balance) > 0.005);
  };

  // Toggle statement selection for custom package
  const toggleStatement = (stmtId) => {
    if (selectedStatements.includes(stmtId)) {
      if (selectedStatements.length > 1) {
        setSelectedStatements(selectedStatements.filter(s => s !== stmtId));
      }
    } else {
      setSelectedStatements([...selectedStatements, stmtId]);
    }
  };

  // Get statements to print based on package option
  const getStatementsToPrint = () => {
    if (effectivePackageOption === 'current') {
      // Map current dashboard statement to print statement
      const mapping = { 'pl': 'pl', 'bs': 'bs', 'cf': 'cf', 'ttm': 't12' };
      return [mapping[currentStatement] || 'pl'];
    }
    if (effectivePackageOption === 'package') {
      // Full package in correct order
      return ['bs', 'pl', 'cf', 'bs_py', 'pl_py', 't12'];
    }
    // Custom - use selected statements in correct order
    const order = ['bs', 'pl', 'cf', 'bs_py', 'pl_py', 't12'];
    return order.filter(s => selectedStatements.includes(s));
  };

  // V18 Step 2.5 — Unsaved-mapping-changes guard.
  //
  // The dashboard's in-memory `mapping` prop is updated by the user via
  // FathomMapLayer's auto-save (~900 ms debounce). If the user clicks
  // Print/Export during that debounce window OR before the layer's
  // auto-save fires, the server has a stale mapping and the PDF would
  // not reflect their latest edits.
  //
  // Strategy: fetch the persisted mapping right before kicking off the
  // server render. If it differs from the dashboard's current mapping
  // (by canonical-line content), surface a 3-button modal:
  //   - "Save and continue" → POST the in-memory mapping, then render
  //   - "Continue anyway"   → render with the persisted (server) state
  //   - "Cancel"            → abort
  //
  // If the persisted and in-memory mappings agree, this is a no-op and
  // the original action proceeds directly.
  const mappingSignature = (m) => {
    if (!m || typeof m !== 'object') return '';
    const sections = Object.keys(m).sort();
    const parts = [];
    sections.forEach((s) => {
      const arr = m[s];
      if (!Array.isArray(arr)) return;
      arr.forEach((line) => {
        const ids = Array.isArray(line?.accounts) ? [...line.accounts].sort().join(',') : '';
        parts.push(`${s}|${line?.label || ''}|${ids}`);
      });
    });
    return parts.join('\n');
  };
  const runWithUnsavedCheck = async (proceed) => {
    if (!client?.id) {
      proceed();
      return;
    }
    let persisted = null;
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/mappings/${client.id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('smj_token')}` }, timeout: 8000 },
      );
      persisted = resp?.data?.mapping || null;
    } catch (_) {
      // If we can't reach the mapping endpoint, skip the check rather
      // than block the user — the render itself will surface server
      // errors with a clearer message.
      proceed();
      return;
    }
    const localSig = mappingSignature(mapping);
    const remoteSig = mappingSignature(persisted);
    if (!localSig || !remoteSig || localSig === remoteSig) {
      proceed();
      return;
    }
    setUnsavedWarning({
      onSaveAndContinue: async () => {
        setUnsavedWarning(null);
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/mappings/${client.id}`,
            { mapping },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('smj_token')}`,
                'X-SMJ-Mapping-Origin': 'staff-coa-editor',
                'Content-Type': 'application/json',
              },
              timeout: 10000,
            },
          );
        } catch (e) {
          // eslint-disable-next-line no-alert
          alert('Could not save the mapping. The PDF will not be generated.');
          return;
        }
        proceed();
      },
      onContinueAnyway: () => {
        setUnsavedWarning(null);
        proceed();
      },
      onCancel: () => setUnsavedWarning(null),
    });
  };


  //
  // The in-modal "Print" button calls /api/pdf/generate (same generator
  // as the dashboard "Export PDF" button). Both entry points therefore
  // read from the PERSISTED canonical mapping on the server, eliminating
  // the V_19 vs Export divergence (e.g. Employee Related Expenses
  // $13,913 vs $15,150).
  //
  // Handoff differs from Export PDF:
  //   - Export PDF  → downloads the file (attachment disposition).
  //   - Print        → opens the file inline in a new browser tab and
  //                    requests an immediate window.print() once the
  //                    PDF viewer has loaded. The user prints from
  //                    Chrome/Edge/Firefox's native PDF print dialog.
  //
  // CROSS-BROWSER NOTE: the embedded-PDF print API is not standardized.
  // We attempt `tab.print()` on load; if the browser's PDF viewer
  // ignores it (Firefox in particular sometimes does), the PDF still
  // opens inline and the user falls back to Ctrl/Cmd+P. The new tab
  // therefore reliably shows the PDF either way.
  // Top-level click handler. Wraps handlePrint with the unsaved-mapping
  // check so the user is warned BEFORE the popup opens (the popup is
  // only opened inside handlePrint after the check resolves to
  // "proceed"). If the popup-blocker kicks in due to the indirect
  // gesture chain, handlePrint falls back to a direct download.
  const handlePrintClick = () => {
    if (pdfGenerating) return;
    runWithUnsavedCheck(() => { handlePrint(); });
  };

  // iOS Safari (iPhone / iPad) silently blocks the popup-print path —
  // window.open() returns a window that's still subject to popup rules
  // and triggers our "Print blocked" toast. Detect and route iOS
  // through the server-side PDF path with `disposition: inline`, which
  // returns a PDF response that iOS opens in a new tab natively (no
  // popup blocker, native Share → Print works).
  const isIOSDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iPad on iOS 13+ reports as MacIntel; the touch-point check
    // disambiguates from desktop Safari.
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    if (ua.includes('Mac') && navigator.maxTouchPoints > 1) return true;
    return false;
  };

  const handlePrint = async () => {
    if (pdfGenerating) return;
    // V21.1 (2026-06-11) — Parameter parity with the dashboard's
    // "Export PDF" button. Previously this caller omitted period_end
    // and base_url, leaving the server to default them — which
    // produced renders for a different month than the modal was
    // displaying, and Playwright opening an env-var-derived base_url
    // that could be wrong on production. Lock both parameters to the
    // same values Export PDF sends, so the two entry points produce
    // byte-identical PDFs (modulo download disposition).
    const period_end = qboMeta?.period_end || null;
    const base_url = window.location.origin;
    // V21.2 (2026-06-11) — Gate on period_end being loaded. The server
    // 422-guard rejects requests with null period_end. If the user
    // clicks Print before qboMeta has resolved (cold load, slow QBO),
    // surface a clear "data still loading" message instead of a
    // confusing "PDF rendering issue" alert from the 422.
    if (!period_end) {
      // eslint-disable-next-line no-alert
      alert(
        'Engagement period is still loading from QuickBooks. ' +
        'Wait a moment and click Print again.'
      );
      return;
    }

    // V21.1 — Popup-blocker workaround for the "click 3-5 times before
    // it works" production complaint. Chrome blocks window.open() that
    // happens AFTER an async await — the user gesture link to the
    // open() call is severed. Fix: open the tab DURING the user-
    // gesture click, write a tiny loading placeholder, then navigate
    // it to the blob URL when the fetch resolves. On error, close it.
    // iOS path is unchanged (Safari handles direct navigation fine
    // and the popup heuristics differ).
    let printWindow = null;
    if (!isIOSDevice()) {
      printWindow = window.open('about:blank', '_blank');
      if (printWindow && printWindow.document) {
        try {
          printWindow.document.write(
            '<!doctype html><html><head><title>Generating PDF…</title>' +
            '<style>body{font-family:-apple-system,sans-serif;display:flex;' +
            'align-items:center;justify-content:center;height:100vh;margin:0;' +
            'background:#f7f7f9;color:#374151}.box{text-align:center}' +
            '.spinner{width:36px;height:36px;border:3px solid #e5e7eb;' +
            'border-top-color:#0085CA;border-radius:50%;margin:0 auto 16px;' +
            'animation:spin 1s linear infinite}' +
            '@keyframes spin{to{transform:rotate(360deg)}}</style>' +
            '</head><body><div class="box"><div class="spinner"></div>' +
            'Generating your financial package…</div></body></html>'
          );
        } catch (_) { /* about:blank in some browsers refuses document.write */ }
      }
    }
    try {
      // V23.3 — Async job flow. The platform ingress kills any HTTP
      // request at ~60s; cold full-package renders can take longer.
      // Start the render as a background job, poll its status (each
      // poll is a fast, ingress-safe request), then fetch the PDF.
      const startResp = await api.post('/pdf/generate-async', {
        client_id: client?.id,
        package: forcePackage || effectivePackageOption || 'package',
        report_type: effectiveReportType,
        period_end,
        base_url,
        disposition: 'inline',
      });
      const jobId = startResp.data.job_id;
      let job = null;
      for (let i = 0; i < 130; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const st = await api.get(`/pdf/jobs/${jobId}`);
        job = st.data;
        if (job.status === 'done' || job.status === 'error') break;
      }
      if (!job || job.status !== 'done') {
        const jobErr = new Error('PDF job failed');
        jobErr.jobDetail = (job && job.status === 'error')
          ? (job.error || 'PDF render failed')
          : 'PDF generation timed out (5 min).';
        jobErr.jobStatus = job?.http_status || 504;
        throw jobErr;
      }
      const resp = await api.get(`/pdf/jobs/${jobId}/download`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      // Open in a new tab so the user can review + Save / Print from
      // the browser's native PDF viewer. window.location.href would
      // navigate away from the dashboard, losing context.
      if (isIOSDevice()) {
        // iOS: replace current page (Safari handles inline PDFs well
        // and Share → Print works from there).
        window.location.href = blobUrl;
      } else {
        // V21.1 — Navigate the pre-opened tab to the blob URL. The
        // tab was opened during the click gesture above (no popup
        // blocker) and now receives the actual PDF.
        if (printWindow && !printWindow.closed) {
          try {
            printWindow.location.href = blobUrl;
          } catch (_) {
            window.open(blobUrl, '_blank');
          }
        } else {
          // Popup never opened (blocked at click) — last-resort fall
          // back to direct navigation.
          window.location.href = blobUrl;
        }
      }
      if (typeof onClose === 'function') onClose();
    } catch (e) {
      // V21.1 — Close the popup placeholder if the render failed so
      // the user isn't left staring at a "Generating…" spinner.
      try { if (printWindow && !printWindow.closed) printWindow.close(); } catch (_) {}
      const status = e?.jobStatus || e?.response?.status;
      const detail = e?.response?.data;
      let parsedDetail = null;
      if (detail) {
        try {
          const text = await (detail instanceof Blob ? detail.text() : Promise.resolve(JSON.stringify(detail)));
          parsedDetail = JSON.parse(text);
        } catch (_) { /* leave null */ }
      }
      // V23.3 — job errors carry the server's `detail` payload directly
      // on the thrown error; HTTP errors keep the parsed-blob path.
      const d = e?.jobDetail !== undefined ? e.jobDetail : parsedDetail?.detail;
      // 503 / 502 → server-side renderer unavailable (Node/Playwright
      // missing on host). Automatically fall back to the legacy popup-
      // print path so the user can still produce a printed package.
      const rendererUnavailable = (status === 503 || status === 502)
        && /node|renderer|playwright|chromium/i.test(typeof d === 'string' ? d : '');
      if (rendererUnavailable || status === 503 || status === 502) {
        try {
          handlePrintPopupLegacy();
          return;
        } catch (_) { /* fall through to alert */ }
      }
      // V21.1 (2026-06-11) — Surface server error JSON correctly. The
      // V21 framework guard + render-error structured payloads return
      // `detail` as an OBJECT, not a string. The old `alert(msg)` line
      // stringified objects as "[object Object]" — opaque, useless.
      // Render the most useful field per payload shape:
      //   • {error, message, ...}                  → message
      //   • {err_name, err_message, failed_phase}  → err_message (phase)
      //   • {failed_ties: [...]}                   → tie list
      //   • string                                 → as-is
      //   • else                                   → JSON.stringify fallback
      let msg = 'PDF generation failed. Please contact support.';
      if (typeof d === 'string') {
        msg = d;
      } else if (d && typeof d === 'object') {
        // V23.3 — err_message/failed_phase/section take precedence over
        // the generic `message` field. Previously `d.message` won, so
        // the user only ever saw "PDF render failed" while the server
        // had already sent WHICH section and phase failed and why.
        if (d.err_message || d.failed_phase) {
          msg = `${d.message || 'PDF render failed'}: ${d.err_message || d.err_name || 'unknown error'}`
            + `${d.section ? ` [section: ${d.section}]` : ''}`
            + `${d.failed_phase ? ` [phase: ${d.failed_phase}]` : ''}`;
        } else if (d.failed_ties) msg = `Validation ties failing: ${(d.failed_ties || []).join(', ')}`;
        else if (d.message) msg = d.message;
        else msg = JSON.stringify(d);
      } else if (status === 422) {
        msg = parsedDetail?.detail || 'PDF request rejected — missing required field.';
      }
      // eslint-disable-next-line no-alert
      alert(msg);
    }
  };

  // Legacy desktop popup-print path. Used as a graceful fallback when
  // the server-side renderer (/api/pdf/generate) is unavailable — e.g.,
  // production containers without Node/Playwright. T12 landscape
  // orientation is best-effort here (browser print dialog ignores CSS
  // @page orientation per-section), but the package still prints.
  const handlePrintPopupLegacy = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow || !printWindow.document) {
      // eslint-disable-next-line no-alert
      alert('Print blocked. Allow popups for this site, or use Export PDF.');
      return;
    }
    const baseHref = window.location.origin;
    const safeTitle = (client?.company || 'Financial Statements').replace(/[<>&"]/g, '');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <base href="${baseHref}/" />
  <style>${PRINT_CSS}</style>
</head>
<body class="mockup-print popup-print-window">
  ${printContent.innerHTML}
</body>
</html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    const triggerPrint = () => {
      try {
        if (printWindow.document.fonts && printWindow.document.fonts.ready) {
          printWindow.document.fonts.ready.then(() => {
            try { printWindow.focus(); } catch (_) { /* ignore */ }
            try { printWindow.print(); } catch (_) { /* ignore */ }
          });
        } else {
          try { printWindow.focus(); } catch (_) { /* ignore */ }
          printWindow.print();
        }
      } catch (_) { /* ignore */ }
    };
    if (printWindow.document.readyState === 'complete') {
      triggerPrint();
    } else {
      printWindow.addEventListener('load', triggerPrint, { once: true });
      // Belt-and-suspenders for browsers that don't fire 'load' on
      // document.write'd popups.
      setTimeout(triggerPrint, 800);
    }
    if (typeof onClose === 'function') onClose();
  };

  // Financial calculations — canonical via lib/reports (sign-normalized, mapping-driven)
  const sum = (ids) => sumAccounts(accounts, ids);
  const getAcc = (id) => getAccountById(accounts, id);

  // Build IS first to get net income, then pass exact NI to BS so the BS
  // equity tie-up lands the displayed sheet on the dollar (Total Assets =
  // Total Liab + Equity on the face).
  const showZeroes = false;
  const isData = consolidatedReports?.is
    ? consolidatedReports.is
    : buildIncomeStatement(accounts, mapping, {
        showZeroes,
        plPeriod: qboMeta?.pl_period,
        plPeriodPrior: qboMeta?.pl_period_prior,
      });
  // Exact (unrounded) NI for BS equity tie-up
  const exactNI = isData.totals.netIncome; // already rounded to whole dollar from unrounded floats
  const bsData = consolidatedReports?.bs
    ? consolidatedReports.bs
    : buildBalanceSheet(accounts, mapping,
        bsNetIncomeAnchor(exactNI, qboMeta), isData.prior.netIncome, { showZeroes });

  // Re-expose the canonical totals under the legacy variable names so the
  // remaining (yet-to-be-canonicalized) renderers compile until they're
  // rewritten in the next phase. All BS/IS numbers here are sign-normalized.
  const totalRevenue  = isData.totals.totalRevenue;
  const totalCOGS     = isData.totals.totalCOGS;
  const grossProfit   = isData.totals.grossProfit;
  const totalOpEx     = isData.totals.totalOpEx;
  const ebit          = isData.totals.ebit;
  const totalOtherIncome  = isData.totals.otherIncome;
  const totalOtherExpense = isData.totals.otherExpense;
  const netIncome     = isData.totals.netIncome;

  // Legacy individual expense breakdowns (only used by template-style renderers
  // that still need replacement). These return canonical signed values.
  const compensation = sum(['6000', '6010', '6020', '6030']);
  const occupancy    = sum(['6100', '6110', '6120']);
  const marketing    = sum(['6200', '6210']);
  const insurance    = getAcc('6300')?.balance || 0;
  const professional = getAcc('6310')?.balance || 0;
  // Pull depreciation from any opex line whose label contains depreciation/amortization
  const depreciation = (mapping?.opex || [])
    .filter(l => /depreciat|amortiz/i.test(l.label || ''))
    .reduce((s, l) => s + sumAccounts(accounts, l.accounts || []), 0);
  const gaOther = sum(['6500', '6510', '6520']);
  const interest = getAcc('6900')?.balance || 0;

  // Balance Sheet — canonical, mapping-driven, sign-normalized
  const cash       = bsData.sections.cash.total;
  const totalCA    = cash + bsData.sections.assetsCur.total;
  const totalFA    = bsData.sections.assetsFix.total;
  const totalOtherAssets = bsData.sections.assetsOther.total;
  const totalAssets      = bsData.totals.totalAssets; // [SANITY-MUTATION reverted]

  const totalCL  = bsData.sections.liabCur.total;
  const totalLTD = bsData.sections.debtShort.total + bsData.sections.liabLong.total;
  const totalLiab = bsData.totals.totalLiab;

  // Equity: BS already tied total equity to (totalAssets - totalLiab) so the
  // displayed sheet foots on the face. Use it directly.
  const equityFromMapping = bsData.totals.totalEquityBeforeNI;
  const totalEquity = bsData.totals.totalEquity;
  const totalLE     = bsData.totals.totalLE;

  // Legacy raw-QBO buckets still referenced by the older PY/T12/CF renderers
  // until those are canonicalized in the next iteration.
  const incomeAccounts       = accounts.filter(a => a.type === 'Income' && a.qbo_type !== 'Other Income');
  const cogsAccounts         = accounts.filter(a => a.type === 'COGS');
  const expenseAccounts      = accounts.filter(a => a.type === 'Expense' && a.qbo_type !== 'Other Expense');
  const otherIncomeAccounts  = accounts.filter(a => a.qbo_type === 'Other Income');
  const otherExpenseAccounts = accounts.filter(a => a.qbo_type === 'Other Expense');
  const equityAccounts       = accounts.filter(a => a.type === 'Equity');
  const fixedAssetAccounts   = accounts.filter(a => a.qbo_type === 'Fixed Asset');
  const otherAssets          = accounts.filter(a => a.qbo_type === 'Other Asset');
  const bankAccounts         = accounts.filter(a => a.qbo_type === 'Bank');
  const arAccounts           = accounts.filter(a => a.qbo_type === 'Accounts Receivable');
  const otherCurrentAssets   = accounts.filter(a => a.qbo_type === 'Other Current Asset');
  const apAccounts           = accounts.filter(a => a.qbo_type === 'Accounts Payable');
  const creditCardAccounts   = accounts.filter(a => a.qbo_type === 'Credit Card');
  const otherCurrentLiab     = accounts.filter(a => a.qbo_type === 'Other Current Liability');
  const longTermLiab         = accounts.filter(a => a.qbo_type === 'Long Term Liability');
  void [incomeAccounts, cogsAccounts, expenseAccounts, otherIncomeAccounts, otherExpenseAccounts,
        equityAccounts, fixedAssetAccounts, otherAssets, bankAccounts, arAccounts,
        otherCurrentAssets, apAccounts, creditCardAccounts, otherCurrentLiab, longTermLiab];

  // Equipment / building shorthands still referenced by the legacy comparison renderers.
  const equipNet  = fixedAssetAccounts.filter(a => (a.name || '').toLowerCase().includes('equip')).reduce((s, a) => s + (a.balance || 0), 0);
  const bldgNet   = fixedAssetAccounts.filter(a => !(a.name || '').toLowerCase().includes('equip')).reduce((s, a) => s + (a.balance || 0), 0);
  const ap        = apAccounts.reduce((s, a) => s + (a.balance || 0), 0);
  const creditCards = creditCardAccounts.reduce((s, a) => s + (a.balance || 0), 0);
  const otherCL   = otherCurrentLiab.reduce((s, a) => s + (a.balance || 0), 0);
  const accruedPay = 0;
  const accruedLib = otherCL;
  const loc       = creditCards;
  const noteEquip = 0;
  const noteBldg  = totalLTD;
  const ar        = arAccounts.reduce((s, a) => s + (a.balance || 0), 0);
  const inventory = otherCurrentAssets.filter(a => (a.name || '').toLowerCase().includes('inventory')).reduce((s, a) => s + (a.balance || 0), 0);
  const stock     = equityAccounts.filter(a => /capital|stock/i.test(a.name || '')).reduce((s, a) => s + (a.balance || 0), 0);
  const retEarnings = totalEquity - stock;
  void [equipNet, bldgNet, ap, creditCards, otherCL, accruedPay, accruedLib, loc, noteEquip, noteBldg, ar, inventory, stock, retEarnings];

  // Cash Flow — sourced from canonical lib/reports.buildCashFlow (same
  // builder the on-screen StaffDashboard CF render consumes). The legacy
  // hardcoded placeholder values that lived here previously have been
  // deleted; do NOT reintroduce them — the publish gate ties against
  // buildCashFlow output, so any divergence between this view and the
  // builder lets fake data through the gate (regression that motivated this
  // wiring).
  //
  // V15: materiality threshold flows in from the client's engagement
  // settings. Null = auto-compute max($1000, 0.5% of total assets).
  // V15.1: niAtPeriodStart + cashAtPeriodStart flow in from qboMeta so the
  // CF differences against the period-start (Dec 31) BS snapshot, not the
  // 12-month-prior BS. This is the period-coherence fix.
  const niPriorExact = isData?.prior?.netIncome ?? 0;
  const cfDataRaw = consolidatedReports?.cf
    ? consolidatedReports.cf
    : buildCashFlow(accounts, mapping, exactNI, niPriorExact, {
        materialityThreshold: client?.cf_materiality_threshold,
        totalAssets: bsData?.totals?.totalAssets || 0,
        niAtPeriodStart: qboMeta?.ni_at_period_start,
        plPeriod: qboMeta?.pl_period,
        // V23.5 — niAtPeriodEnd anchors the BS NetIncome-row movement and
        // must be fiscal-YTD NI, not period NI. For mid-FY periods (Q2 etc.)
        // period NI understates the equity movement by ni_at_period_start.
        niAtPeriodEnd: bsNetIncomeAnchor(exactNI, qboMeta),
      });
  // Apply render-time display footing reconciliation + footing assertions.
  // Throws (with a structured diagnostic) if any printed total doesn't
  // foot — blocks the render rather than shipping a non-footing face.
  const cfData = prepareCfForDisplay(cfDataRaw);
  let footingError = null;
  try {
    assertCfFootsForRender(cfData);
    if (bsData) assertBsFootsForRender(bsData);
  } catch (e) {
    // V23.3 — DO NOT re-throw. Throwing during React render with no
    // error boundary unmounted the ENTIRE app (blank screen), turning a
    // validation refusal into a hard crash. Instead render a blocking
    // panel below; the headless renderer detects [data-render-blocked]
    // and refuses to ship the PDF, preserving the strict footing gate.
    // eslint-disable-next-line no-console
    console.error('[render-footing-assertion]', e.message, e.diagnostic);
    footingError = e;
  }
  if (footingError) {
    const blockedPanel = (
      <div
        data-render-blocked="1"
        data-render-blocked-message={footingError.message}
        data-testid="print-render-blocked-panel"
        style={{
          background: '#FEF2F2', border: '2px solid #DC2626', borderRadius: 8,
          padding: '24px', margin: '24px', fontFamily: 'monospace', color: '#7F1D1D',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Render blocked — statements do not foot</h2>
        <p style={{ whiteSpace: 'pre-wrap' }}>{footingError.message}</p>
        <p style={{ fontSize: 12, color: '#991B1B' }}>
          The PDF will not be generated until the underlying data ties. Use the
          Download Diagnostic button on the dashboard to capture the failing state.
        </p>
      </div>
    );
    if (headless) return <div data-testid="print-headless-root">{blockedPanel}</div>;
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto" data-testid="print-modal-blocked-overlay">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mt-16">
          {blockedPanel}
          <div style={{ padding: '0 24px 24px' }}>
            <button
              type="button"
              data-testid="print-blocked-close-btn"
              onClick={() => {
                document.body.classList.remove('mockup-print');
                if (typeof onClose === 'function') onClose();
              }}
              className="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Prior year factor for comparison reports — TODO: replace with real prior_balance lookups
  const pyFactor = 0.85;

  const fmt = (v) => formatCurrency(v);
  const pct = (v) => formatPercent(v, totalRevenue);

  const getPeriodEndDate = () => {
    // V15.2: use the canonical period label derived from qboMeta so the
    // cover page reads "Four Months Ended April 30, 2026" — matching the
    // IS/CF period header — instead of the legacy "April 2026" trailing-
    // month label which implied a single-month report.
    if (qboMeta?.period_end) {
      const { monthsEndedLabel } = defaultPeriodLabels(qboMeta);
      return monthsEndedLabel;
    }
    const now = new Date();
    if (periodId === 'prior_month') {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return periodRange?.label || now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Get the fiscal year for statements based on selected period
  const getFiscalYear = () => {
    if (periodId === 'last_year') {
      return new Date().getFullYear() - 1;
    }
    if (periodRange?.end) {
      return periodRange.end.getFullYear();
    }
    return new Date().getFullYear();
  };

  const currentYear = getFiscalYear();
  const priorYear = currentYear - 1;

  const getCurrentDateFormatted = () => {
    return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Footer text — driven by client engagement_type (AR-C 70 / AR-C 80 / internal),
  // disclosures_omitted, and effectiveReportType (the staff's modal selection
  // for current run). Clients still see no footer.
  //
  // AR-C 70 (preparation): include the omitted-disclosures phrase when the
  // engagement has disclosures_omitted=True. Drop "Unaudited" — redundant
  // with "no assurance is provided" per the user's Part A.6 directive.
  // AR-C 80 (compilation): footer reads "See accountant's compilation report."
  // (each statement page, per AR-C 80 illustrative reports).
  const engagementType = client?.engagement_type || 'preparation';
  // V19 — Use the effective framework (centralized helper). On prepared
  // engagements this ALWAYS returns 'none' regardless of the
  // client.framework dropdown value, so the AR-C 70.A6 disclosure
  // language renders on every prepared package. On compilation
  // engagements, returns client.framework unchanged so the V17
  // compilation behavior is preserved.
  const framework = getEffectiveFramework(client);
  const frameworkOtherLabel = client?.framework_other_label || '';
  const disclosuresOmitted = client?.disclosures_omitted !== false; // default true
  const independent = client?.independent !== false; // default true
  // `signing_member` is intentionally NOT read here. AR-C 80 illustrative
  // reports end with the firm signature alone (Item 1, 2026-05-21 push);
  // the field is preserved on the client schema for use in other
  // deliverables (engagement letter cover, audit trail) but is not
  // rendered on the compilation report.
  const engagementMeta = ENGAGEMENT_REPORTS[engagementType] || ENGAGEMENT_REPORTS.preparation;
  const periodEndDate = periodRange?.end ? periodRange.end.toISOString().slice(0, 10) : null;
  const periodStartDate = periodRange?.start ? periodRange.start.toISOString().slice(0, 10) : null;
  const interim = isInterimPeriod(periodStartDate, periodEndDate);

  // FOOTER CONVENTION (Item 4, 2026-05-21 push)
  //
  // Prepared (AR-C 70):
  //   GAAP, disclosures omitted:
  //     "No assurance is provided on these financial statements. ·
  //      Prepared in accordance with accounting principles generally
  //      accepted in the United States of America · Substantially all
  //      disclosures are omitted"
  //   Non-GAAP, disclosures omitted:
  //     "No assurance is provided on these financial statements. ·
  //      Prepared on the [framework] · Substantially all disclosures
  //      are omitted"
  //   Disclosures NOT omitted: drop the final segment.
  //
  // Compilation (AR-C 80):
  //   GAAP: "See accountant's compilation report." (single segment)
  //   Non-GAAP: "See accountant's compilation report. · Prepared on
  //              the [framework]"
  //
  // "Unaudited" is dropped from all footers — redundant in both
  // engagement types.
  // V19 — Statement-page footer composition. V_19.pdf reference uses
  // a 3-segment structure joined by middle-dot bullets:
  //   "No assurance is provided on these financial statements ·
  //    {framework clause} ·
  //    Substantially all disclosures are omitted"
  //
  // For PREPARED engagements (all frameworks, per V19 spec — the
  // framework dropdown does NOT drive output text), the middle clause
  // is the AR-C 70.A6 wording:
  //   "The financial statements have not been prepared in accordance
  //    with an applicable financial reporting framework and may not be
  //    appropriate for users who are not informed about the basis of
  //    accounting used"
  //
  // For COMPILATION engagements, V17 compilation behavior is preserved
  // via buildCompiledFooter (see below).
  const buildPreparedFooter = () => {
    const segments = ['No assurance is provided on these financial statements'];
    // framework is always 'none' on prepared engagements per
    // getEffectiveFramework. Use the AR-C 70.A6 middle clause directly.
    segments.push(
      'The financial statements have not been prepared in accordance with an applicable financial reporting framework and may not be appropriate for users who are not informed about the basis of accounting used'
    );
    if (disclosuresOmitted) {
      segments.push('Substantially all disclosures are omitted');
    }
    return segments.join(' · ');
  };
  const buildCompiledFooter = () => {
    const segments = ["See accountant's compilation report."];
    const fwSegment = getCompiledFooterFrameworkPhrase(framework, frameworkOtherLabel);
    if (fwSegment) segments.push(fwSegment);
    return segments.join(' · ');
  };
  // Interim stamp is intentionally NOT appended to either prepared or
  // compiled footers under the new convention — the framework segment
  // already identifies the engagement context, and "Interim — Unaudited"
  // conflicted with both AR-C 70 and AR-C 80 wording.
  let footerText;
  if (isClient) {
    footerText = '';
  } else if (effectiveReportType === 'compiled') {
    footerText = buildCompiledFooter();
  } else if (engagementType === 'internal') {
    footerText = engagementMeta.footer;
  } else {
    footerText = buildPreparedFooter();
  }
  // Day-3 cutover — per-statement footer is the short single-line
  // form. The full SSARS legend lives on the cover page bottom block
  // (see renderCoverPage).
  if (isClient) {
    footerText = '';
  } else if (effectiveReportType === 'compiled') {
    const fwLabel = (() => {
      if (framework === 'gaap') return null;
      if (framework === 'income_tax_basis') return 'income tax basis';
      if (framework === 'cash_basis') return 'cash basis';
      if (framework === 'modified_cash_basis') return 'modified cash basis';
      if (framework === 'other' && frameworkOtherLabel) return frameworkOtherLabel.toLowerCase();
      return null;
    })();
    footerText = fwLabel
      ? `See accountant's compilation report · Prepared on the ${fwLabel}`
      : `See accountant's compilation report`;
  } else {
    // Prepared / AR-C 70 — short single-line. The full A6 disclosure
    // is on the cover. Always include "no assurance" + omitted-
    // disclosures phrase when applicable.
    const segs = ['No assurance is provided'];
    if (disclosuresOmitted) segs.push('Substantially all disclosures omitted');
    footerText = segs.join(' · ');
  }
  // `interim` variable is now used only to optionally surface interim
  // status in cover/period labels (e.g. "for the period ended" vs
  // "for the year ended" in the compilation report wording). Keep
  // for that purpose.
  void interim;

  // Map statement IDs to their formal names for the compilation report.
  // Framework-aware: when engagement framework is non-GAAP, the title shifts
  // ("Statement of Assets, Liabilities, and Equity — Income Tax Basis", etc).
  const getStatementName = (stmtId) => getStatementTitle(stmtId, framework, frameworkOtherLabel);

  // Format the list of statements for the compilation report.
  // Per Part A.8 (TTM as Supplementary Information for compiled engagements):
  // the T12 is EXCLUDED from the compilation-covered statement list and
  // moved into the supplementary-information paragraph instead.
  const formatStatementList = () => {
    let statements = getStatementsToPrint();
    if (effectiveReportType === 'compiled') {
      statements = statements.filter(s => s !== 't12');
    }
    const names = statements.map(s => getStatementName(s));

    if (names.length === 0) return '';
    if (names.length === 1) return `the ${names[0]}`;
    if (names.length === 2) return `the ${names[0]} and the ${names[1]}`;

    // For 3+ items: "the A, the B, and the C"
    const allButLast = names.slice(0, -1).map(n => `the ${n}`).join(', ');
    const last = names[names.length - 1];
    return `${allButLast}, and the ${last}`;
  };

  // Does the current print package include the T12? (Drives whether the
  // compilation report references supplementary information.)
  const packageIncludesT12 = () =>
    getStatementsToPrint().includes('t12');

  // COVER PAGE — always renders for both prepared AND compiled packages.
  // Per Part A.9 (V9 regression fix): compiled packages were previously
  // showing a blank cover page; the cover should render identically for
  // both engagement types, with the compilation report page inserted
  // AFTER the cover. Client view continues to render the cover too.
  //
  // V19 — Cover is intentionally minimal per V_19 reference: logo +
  // firm name + "Financial Statements" + period descriptor. The
  // framework disclosure (AR-C 70.A6 verbatim on prepared, framework
  // phrase on compilation) renders ONLY on each statement page footer,
  // NOT on the cover. See V19 spec §1.7.
  const renderCoverPage = () => {
    // Day-3 cutover — structured cover with cover-title-block on top
    // half, and a bottom block containing the firm contact info + full
    // SSARS disclosure. Firm contact is placeholder text until
    // firm-self record carries the real address/phone/website.
    const fullSsarsLegend = (() => {
      // Surface the AR-C 70.A6 or AR-C 80 legend on the cover, since
      // per-statement footers go short.
      const segments = [];
      if (effectiveReportType === 'compiled') {
        segments.push("See accountant's compilation report.");
        const fwSeg = getCompiledFooterFrameworkPhrase(framework, frameworkOtherLabel);
        if (fwSeg) segments.push(fwSeg);
      } else {
        segments.push('No assurance is provided on these financial statements.');
        segments.push(
          'The financial statements have not been prepared in accordance with an applicable financial reporting framework and may not be appropriate for users who are not informed about the basis of accounting used.'
        );
        if (disclosuresOmitted) {
          segments.push('Substantially all disclosures are omitted.');
        }
      }
      return segments.join(' ');
    })();
    return (
      <div className="page">
        <div className="cover-page">
          <div className="cover-spacer-top" />
          <div className="cover-title-block">
            {/* V19 (2026-06-10) — cover-page logo block removed per Brandon.
                The logo remains on the accountant's report letterhead
                unchanged; only the cover-page banner is dropped. Title
                block, period line, firm block, and disclosure legend
                are untouched. */}
            <div className="cover-company">{client?.company || 'Company Name'}</div>
            <div className="cover-title">
              {effectiveReportType === 'compiled' ? 'Compiled Financial Statements' : 'Financial Statements'}
            </div>
            <div className="cover-period">{getPeriodEndDate()}</div>
          </div>
          <div className="cover-footer-block">
            <div className="cover-firm-contact">
              <div className="firm-name">Steele Martin Jones &amp; Co., PLC</div>
              <div>100 Stonebridge Blvd, Jackson, TN 38305 · 731-668-9183 · steelemartin.com</div>
              <div>Member AICPA</div>
            </div>
            <div className="cover-ssars-legend">
              {fullSsarsLegend}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ACCOUNTANT'S COMPILATION REPORT (AR-C 80)
  //
  // SOURCE: AICPA AR-C Section 80 illustrative reports (illustrations 1
  // through 4) as published in SSARS No. 25, effective for periods
  // ending on or after December 15, 2021. The opening paragraph follows
  // the current illustrative form which begins with management's
  // responsibility — NOT the pre-2021 form. The wording below is
  // VERBATIM from the illustrative reports; do not paraphrase.
  //
  // PARAGRAPHS, in order:
  //   1. Management responsibility + accountant's engagement disclaimer.
  //   2. (Conditional, when client.disclosures_omitted = true)
  //      Omitted-disclosures paragraph — AR-C 80.A11 / illustration 4.
  //   3. (Conditional, when client.independent = false)
  //      Independence statement — AR-C 80.18.
  //   3.5. (Conditional, when client.framework != "gaap")
  //      Basis of Accounting paragraph — AR-C 80.A24-A26.
  //      Cross-references the Description of Basis of Accounting page.
  //   4. (Conditional, when package includes T12)
  //      Supplementary-information paragraph — AR-C 80.A13.
  //   5. Signature block: firm name typed + location + report date.
  //      AR-C 80 illustrative reports end with firm signature ALONE;
  //      no "By: [Member]" line.
  //
  // The "applicable financial reporting framework" phrase is parameterized
  // from `client.framework` (Part A.1). For non-GAAP engagements, the
  // framework label shifts (e.g. "the income tax basis of accounting").
  const renderCompilationReport = () => {
    const frameworkPhrase = getFrameworkPhrase(framework, frameworkOtherLabel);
    const statementList = formatStatementList();
    const periodLabel = (() => {
      // V17 fix — read the lowercase single-period form ("four months
      // ended April 30, 2026") directly, then prefix "for the". The
      // V16 bug was reading monthsEndedLabel ("Four Months Ended April
      // 30, 2026"), then prefixing the legacy "for the period ended"
      // template, producing "for the period ended Four Months Ended
      // April 30, 2026" (double "ended").
      if (qboMeta?.period_end) {
        const { singlePeriodLowercaseLabel } = defaultPeriodLabels(qboMeta);
        return `for the ${singlePeriodLowercaseLabel}`;
      }
      // Legacy fallback (no qboMeta) — best-effort approximation.
      const eod = getPeriodEndDate();
      if (interim) return `for the period ended ${eod}`;
      return `for the year ended ${eod}`;
    })();
    return (
      <div className="page">
        <div className="compilation-content">
          <div className="compilation-logo">
            <img src={SMJ_LOGO_URL} alt="Steele Martin Jones" />
          </div>

          <h2>Accountant's Compilation Report</h2>

          <p>To the Management of {client?.company || 'Company Name'}:</p>

          {/* Paragraph 1 — verbatim from AR-C 80 illustrative reports.
              Opens with management's responsibility, not the accountant's
              actions. SSARS-25 form. */}
          <p>
            Management is responsible for the accompanying financial
            statements of {client?.company || 'Company Name'}, which
            comprise {statementList} as of and {periodLabel}, in accordance
            with {frameworkPhrase}. We have performed the compilation
            engagement in accordance with Statements on Standards for
            Accounting and Review Services promulgated by the Accounting
            and Review Services Committee of the AICPA. We did not audit
            or review the financial statements nor were we required to
            perform any procedures to verify the accuracy or completeness
            of the information provided by management. Accordingly, we do
            not express an opinion, a conclusion, nor provide any form of
            assurance on these financial statements.
          </p>

          {/* Paragraph 2 — omitted disclosures (AR-C 80.A11). Only when
              client.disclosures_omitted = true. */}
          {disclosuresOmitted && (
            <p>
              Management has elected to omit substantially all of the
              disclosures ordinarily included in financial statements
              prepared in accordance with {frameworkPhrase}. If the
              omitted disclosures were included in the financial
              statements, they might influence the user's conclusions
              about the Company's financial position, results of
              operations, and cash flows. Accordingly, the financial
              statements are not designed for those who are not informed
              about such matters.
            </p>
          )}

          {/* Paragraph 3 — independence statement (AR-C 80.18). Only when
              independence is impaired. No statement of independence is
              required when independent; silence implies independence. */}
          {!independent && (
            <p>
              We are not independent with respect to {client?.company || 'Company Name'}.
            </p>
          )}

          {/* Paragraph 3.5 — Basis of Accounting (Item 6, 2026-05-21 push;
              AR-C 80.A24-A26). Required for compilation engagements with a
              framework other than GAAP. Cross-references the Description
              of Basis of Accounting page (Item 7), which is locked ON
              for compilation + non-GAAP. */}
          {framework !== 'gaap' && (
            <p>
              The financial statements are prepared in accordance with {frameworkPhrase},
              which is a basis of accounting other than accounting principles
              generally accepted in the United States of America. {frameworkPhrase.charAt(0).toUpperCase() + frameworkPhrase.slice(1)} is
              described in the Description of Basis of Accounting included
              with these financial statements.
            </p>
          )}

          {/* Paragraph 4 — supplementary information (AR-C 80.A13).
              Only when the package includes the T12 / TTM Statement of
              Income, which is presented as supplementary information for
              compiled engagements per Part A.8. */}
          {packageIncludesT12() && (
            <p>
              Management has also presented the trailing twelve months
              statement of income, which is presented for purposes of
              additional analysis and is not a required part of the basic
              financial statements. We have not audited, reviewed, or
              compiled the supplementary information and, accordingly,
              do not express an opinion, a conclusion, nor provide any
              assurance on it.
            </p>
          )}

          {/* Paragraph 5 — signature block per Part A.4 + Item 1
              (2026-05-21 push): typed firm name in normal weight,
              location and report date below. AICPA AR-C 80 illustrative
              reports end with the firm signature ALONE — no "By:
              [Member]" line. signing_member is retained in the client
              schema for use in other deliverables (engagement letter,
              etc.) but is not rendered here. */}
          <div className="signature-block">
            <div className="firm-name">Steele Martin Jones &amp; Co., PLC</div>
            <div className="firm-location">Jackson, Tennessee</div>
            <div className="firm-location">{getCurrentDateFormatted()}</div>
          </div>
        </div>
      </div>
    );
  };

  // DESCRIPTION OF BASIS OF ACCOUNTING page (Item 7, 2026-05-21 push).
  //
  // Required for compilation engagements with framework != GAAP (per
  // AR-C 80.A24-A26); the compilation report's basis-of-accounting
  // paragraph (§3.5 in renderCompilationReport) cross-references this
  // page. Optional for prepared + non-GAAP engagements; staff opt-in
  // via the Engagement Settings panel.
  //
  // The page content is staff-configurable per engagement via
  // `client.description_of_basis_text`. When the staff have not
  // customized the text, we fall back to a per-framework default
  // that matches the AICPA OCBOA guidance (AR-C §80.A24-A26 examples).
  const DESCRIPTION_OF_BASIS_DEFAULTS = {
    // V19 (2026-06-10) — SMJ-aligned verbatim copy approved by Brandon.
    // This replaces the pre-V19 income-tax-basis default which contradicted
    // the compilation report (which states "no applicable financial reporting
    // framework"). The new copy is engagement-agnostic for any non-GAAP
    // framework that elects no applicable framework; clients on a TRUE
    // tax/cash/modified-cash basis should override via
    // `client.description_of_basis_text`. The compilation report language
    // is intentionally NOT changed by this update.
    income_tax_basis:
      'These financial statements have not been prepared in accordance with an applicable financial reporting framework, such as accounting principles generally accepted in the United States of America. The statements are prepared on the basis of accounting used by the Company for internal reporting, which reflects the Company\u2019s elections as to the recognition, measurement, and presentation of its accounts. This basis differs from accounting principles generally accepted in the United States of America, and the differences have not been determined. Certain items, including partner compensation, are presented in a manner the Company has elected for its internal reporting purposes.',
    cash_basis:
      'These financial statements have been prepared on the cash basis of accounting. Under this basis, revenue is recognized when received and expenses are recognized when paid. This basis of accounting differs from accounting principles generally accepted in the United States of America primarily because revenue is not recognized as earned and expenses are not recognized as incurred; accounts receivable, accounts payable, accrued liabilities, and prepaid expenses are not recorded.',
    modified_cash_basis:
      'These financial statements have been prepared on the modified cash basis of accounting. Under this basis, revenue is recognized when received and expenses are recognized when paid, except that certain assets are capitalized and depreciated and certain liabilities are recorded as incurred. This basis differs from accounting principles generally accepted in the United States of America primarily in the treatment of accounts receivable, accounts payable, and accrued liabilities.',
    other:
      'These financial statements have not been prepared in accordance with an applicable financial reporting framework, such as accounting principles generally accepted in the United States of America. The statements are prepared on the basis of accounting used by the Company for internal reporting, which reflects the Company\u2019s elections as to the recognition, measurement, and presentation of its accounts. This basis differs from accounting principles generally accepted in the United States of America, and the differences have not been determined. Certain items, including partner compensation, are presented in a manner the Company has elected for its internal reporting purposes.',
  };

  // Item 7 lock logic. Description of Basis renders when the
  // effective report (per the user's PrintModal selection or the
  // staff "preview as compiled" engagement override) is compiled and
  // the framework is not GAAP — same gate that drives the
  // Compilation Report itself, so the two stay in sync. Earlier
  // version keyed off `engagementType` (the STORED engagement_type)
  // which caused the Description of Basis to silently drop whenever
  // an engagement stored as 'preparation' was rendered as compiled
  // via the engagement_override flag (e.g. SMJ on 2026-06-01).
  const descLockedOn = effectiveReportType === 'compiled' && framework !== 'gaap';
  const descLockedOff = framework === 'gaap';
  const shouldRenderDescBasis = !isClient && (
    descLockedOn ||
    (!descLockedOff && client?.include_description_of_basis === true)
  );

  const renderDescriptionOfBasisPage = () => {
    const fallback = DESCRIPTION_OF_BASIS_DEFAULTS[framework] || DESCRIPTION_OF_BASIS_DEFAULTS.other;
    const text = (client?.description_of_basis_text || '').trim() || fallback;
    // V21 (2026-06-11) — Heading derives from the engagement framework
    // setting so the Description page can NEVER reference a different
    // basis than the compilation report. Single source of truth: the
    // FRAMEWORKS map keyed by client.framework.
    //   • income_tax_basis      → "Description of the Income Tax Basis of Accounting"
    //   • cash_basis            → "Description of the Cash Basis of Accounting"
    //   • modified_cash_basis   → "Description of the Modified Cash Basis of Accounting"
    //   • other                 → "Description of the [client-provided label]"
    //   • gaap / fallback       → "Description of Basis of Accounting"  (generic;
    //                              GAAP normally doesn't render this page at all)
    //   • none                  → "Description of Basis of Accounting"  (preparation
    //                              fallback; the .A6 path uses a different page entirely)
    const headingByFramework = {
      income_tax_basis:    'Description of the Income Tax Basis of Accounting',
      cash_basis:          'Description of the Cash Basis of Accounting',
      modified_cash_basis: 'Description of the Modified Cash Basis of Accounting',
    };
    const fwOtherHeading = (framework === 'other' && (frameworkOtherLabel || '').trim())
      ? `Description of the ${frameworkOtherLabel.trim()}`
      : null;
    const pageHeading =
      headingByFramework[framework] ||
      fwOtherHeading ||
      'Description of Basis of Accounting';
    return (
      <div className="page">
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0 0.25in' }}>
          <div className="header" style={{ marginBottom: 24 }}>
            <div className="company">{client?.company || 'Company Name'}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div className="statement-title" style={{ textAlign: 'left', marginBottom: 20 }}>
              {pageHeading}
            </div>
            {text.split(/\n\s*\n/).map((para, i) => (
              <p key={i} style={{
                fontSize: 11, lineHeight: 1.65, textAlign: 'left',
                marginBottom: 14, color: '#1A1D21',
              }}>
                {para.trim()}
              </p>
            ))}
          </div>
          {footerText && <div className="footer">{footerText}</div>}
        </div>
      </div>
    );
  };

  // BALANCE SHEET — single column, mapping-driven, sign-normalized via lib/reports
  const renderBalanceSheet = () => {
    const { sections } = bsData;
    const { asOfLabel } = defaultPeriodLabels(qboMeta);
    // Cash is now auto-promoted as a group inside Current Assets by
    // buildBalanceSheet; render the structured `items` directly.
    const totalCurrentAssets = sections.assetsCur.totalCombined;

    // Walk structured items (group + leaf) for any section.
    //   leaf  -> single indented row
    //   group -> bold header at base indent, indented leaves, group subtotal
    const renderItems = (items, baseIndent = 1) => {
      if (!items || items.length === 0) return null;
      return items.map((it, idx) => {
        if (it.kind === 'group') {
          return (
            <React.Fragment key={`bs-g-${idx}-${it.label}`}>
              <tr className="indent" style={{ fontWeight: 600 }}>
                <td style={{ paddingLeft: baseIndent * 6 }}>{it.label}</td><td></td>
              </tr>
              {it.leaves.map((l, li) => (
                <tr key={`bs-l-${idx}-${li}`} className="indent">
                  <td style={{ paddingLeft: (baseIndent + 1) * 6 }}>{normalizeLeafLabel(l.label)}</td>
                  <td>{fmt(l.current)}</td>
                </tr>
              ))}
              <tr className="subtotal">
                <td style={{ paddingLeft: baseIndent * 6 }}>Total {it.label}</td>
                <td>{fmt(it.subtotal)}</td>
              </tr>
            </React.Fragment>
          );
        }
        return (
          <tr key={`bs-leaf-${idx}`} className="indent">
            <td style={{ paddingLeft: baseIndent * 6 }}>{it.label}</td>
            <td>{fmt(it.current)}</td>
          </tr>
        );
      });
    };
    return (
    <div className="page">
      <div className="header">
        <div className="company">{client?.company || 'Company Name'}</div>
      </div>
      <div className="statement-content balance-sheet">
        <div className="statement-title">{getStatementTitle('bs', framework, frameworkOtherLabel)}</div>
        <div className="statement-period">{asOfLabel}</div>

        {/* Single continuous BS table. Assets and Liabilities + Equity
            share the same `<table>` so the AMOUNT column rhythm is
            preserved exactly: a vertical ruler dropped from TOTAL ASSETS
            lands on the dollar amount of TOTAL LIABILITIES AND
            MEMBERS' EQUITY. The L+E side transition is marked with a
            major-section-header row (same row structure as
            CURRENT ASSETS / PROPERTY & EQUIPMENT — label in the
            description column, empty AMOUNT cell — plus a thin top
            rule via the .major-section-header modifier class). */}
        <table className="balance-sheet-table">
          <thead><tr><th>ASSETS</th><th>AMOUNT</th></tr></thead>
          <tbody>
            <tr className="section-header"><td>Current Assets</td><td></td></tr>
            {renderItems(sections.assetsCur.items, 1)}
            <tr className="subtotal"><td>Total Current Assets</td><td>{fmt(totalCurrentAssets)}</td></tr>

            {sections.assetsFix.items.length > 0 && (
              <>
                <tr className="section-header"><td>Property & Equipment</td><td></td></tr>
                {renderItems(sections.assetsFix.items, 1)}
                <tr className="subtotal"><td>Total Property & Equipment</td><td>{fmt(sections.assetsFix.total)}</td></tr>
              </>
            )}
            {sections.assetsOther.items.length > 0 && (
              <>
                <tr className="section-header"><td>Other Assets</td><td></td></tr>
                {renderItems(sections.assetsOther.items, 1)}
                <tr className="subtotal"><td>Total Other Assets</td><td>{fmt(sections.assetsOther.total)}</td></tr>
              </>
            )}
            <tr className="total"><td>TOTAL ASSETS</td><td>{fmt(totalAssets)}</td></tr>

            {/* Major-section transition — asset side concluding,
                liability side starting. Same row structure as a
                regular section-header; thin top rule + extra breathing
                room applied via .major-section-header class. */}
            <tr className="section-header major-section-header"><td>LIABILITIES AND {equityLabels.equity.toUpperCase()}</td><td></td></tr>

            {sections.liabCur.items.length > 0 && (
              <>
                <tr className="section-header"><td>Current Liabilities</td><td></td></tr>
                {renderItems(sections.liabCur.items, 1)}
                <tr className="subtotal"><td>Total Current Liabilities</td><td>{fmt(sections.liabCur.total)}</td></tr>
              </>
            )}
            {sections.debtShort.items.length > 0 && (
              <>
                <tr className="section-header"><td>Short-Term Debt</td><td></td></tr>
                {renderItems(sections.debtShort.items, 1)}
                <tr className="subtotal"><td>Total Short-Term Debt</td><td>{fmt(sections.debtShort.total)}</td></tr>
              </>
            )}
            {sections.liabLong.items.length > 0 && (
              <>
                <tr className="section-header"><td>Long-Term Liabilities</td><td></td></tr>
                {renderItems(sections.liabLong.items, 1)}
                <tr className="subtotal"><td>Total Long-Term Liabilities</td><td>{fmt(sections.liabLong.total)}</td></tr>
              </>
            )}
            <tr className="subtotal"><td>TOTAL LIABILITIES</td><td>{fmt(totalLiab)}</td></tr>

            <tr className="section-header"><td>{equityLabels.equity}</td><td></td></tr>
            {renderItems(sections.equity.items, 1)}
            <tr className="indent"><td>Current Year Earnings</td><td>{fmt(netIncome)}</td></tr>
            <tr className="subtotal"><td>Total {equityLabels.equity}</td><td>{fmt(totalEquity)}</td></tr>

            <tr className="grand-total"><td>TOTAL LIABILITIES AND {equityLabels.equity.toUpperCase()}</td><td>{fmt(totalLE)}</td></tr>
          </tbody>
        </table>
      </div>
      {footerText && <div className="footer">{footerText}</div>}
    </div>
    );
  };

  // STATEMENT OF INCOME — mapping-driven, sign-normalized via lib/reports
  const renderIncomeStatement = () => {
    const { sections, totals } = isData;
    const { forPeriodLabel, monthsEndedLabel } = defaultPeriodLabels(qboMeta);
    // Day-1 mockup — SSARS-style period descriptor "For the Four Months
    // Ended April 30, 2026" (instead of "For the period Jan 1 through
    // Apr 30, 2026"). Item #9 of the presentation pass.
    const isPeriodLabel = `For the ${monthsEndedLabel}`;
    const pctOf = (v) => totals.totalRevenue ? ((v / totals.totalRevenue) * 100).toFixed(1) + '%' : '—';
    const renderLines = (sec, opts = {}) => sec.lines.map((l, i) => (
      <tr key={`${sec.key}-${i}`} className="indent">
        <td>{normalizeLeafLabel(l.label)}</td>
        <td>{fmt(opts.negate ? -l.current : l.current)}</td>
        <td>{pctOf(opts.negate ? -l.current : l.current)}</td>
      </tr>
    ));

    const hasOtherInc = sections.otherIncome.lines.length > 0;
    const hasOtherExp = sections.otherExpense.lines.length > 0;

    return (
    <div className="page">
      <div className="header">
        <div className="company">{client?.company || 'Company Name'}</div>
      </div>
      <div className="statement-content">
        <div className="statement-title">{getStatementTitle('pl', framework, frameworkOtherLabel)}</div>
        <div className="statement-period">{isPeriodLabel}</div>

        <table>
          <thead><tr><th>DESCRIPTION</th><th>AMOUNT</th><th>% REV</th></tr></thead>
          <tbody>
            <tr className="section-header"><td>REVENUE</td><td></td><td></td></tr>
            {renderLines(sections.income)}
            <tr className="subtotal"><td>Total Revenue</td><td>{fmt(totals.totalRevenue)}</td><td>100.0%</td></tr>

            {sections.cogs.lines.length > 0 && (
              <>
                <tr className="section-header"><td>COST OF REVENUE</td><td></td><td></td></tr>
                {renderLines(sections.cogs)}
                <tr className="subtotal"><td>Total Cost of Revenue</td><td>{fmt(totals.totalCOGS)}</td><td>{pctOf(totals.totalCOGS)}</td></tr>
              </>
            )}
            <tr className="subtotal is-gross-profit"><td>GROSS PROFIT</td><td>{fmt(totals.grossProfit)}</td><td>{pctOf(totals.grossProfit)}</td></tr>

            <tr className="section-header"><td>OPERATING EXPENSES</td><td></td><td></td></tr>
            {renderLines(sections.opex)}
            <tr className="subtotal"><td>Total Operating Expenses</td><td>{fmt(totals.totalOpEx)}</td><td>{pctOf(totals.totalOpEx)}</td></tr>

            <tr className="subtotal is-income-from-ops"><td>INCOME FROM OPERATIONS</td><td>{fmt(totals.ebit)}</td><td>{pctOf(totals.ebit)}</td></tr>

            {(hasOtherInc || hasOtherExp) && (
              <>
                <tr className="section-header"><td>OTHER INCOME / (EXPENSE)</td><td></td><td></td></tr>
                {renderLines(sections.otherIncome)}
                {renderLines(sections.otherExpense, { negate: true })}
              </>
            )}

            <tr className="grand-total"><td>NET INCOME</td><td>{fmt(totals.netIncome)}</td><td>{pctOf(totals.netIncome)}</td></tr>
          </tbody>
        </table>
      </div>
      {footerText && <div className="footer">{footerText}</div>}
    </div>
    );
  };

  // STATEMENT OF CASH FLOWS — sourced entirely from canonical buildCashFlow.
  // No placeholder math, no hardcoded line values. The DRAFT banner only
  // shows when buildCashFlow's hasPrior=false (no PY data) — once prior-year
  // data is present, the canonical math runs and the validation gate
  // (cf_ni_ties_to_is + cf_ending_cash_ties_to_bs) controls publishability.
  const renderCashFlow = () => {
    const { monthsEndedLabel } = defaultPeriodLabels(qboMeta);
    // SSARS-style period descriptor (item #9).
    const cfPeriodLabel = `For the ${monthsEndedLabel}`;
    // Day-3 cutover — CF zero suppression is now production behavior.
    // Rows with |value| < $0.50 are hidden by default; staff can flip
    // the in-modal "Show zero lines" toggle for completeness review.
    // Section subtotals always render (their presence is
    // structurally meaningful even at $0).
    const suppressZero = !showZeroLines;
    const renderRow = (r, i) => {
      const isSubheader = r.subheader === true;
      const isRollup = r.rollup === true;
      const isZeroSuppressed = suppressZero
        && !isSubheader
        && r.value != null
        && Math.abs(Number(r.value) || 0) < 0.5;
      const style = isSubheader
        ? { fontWeight: 600, fontStyle: 'italic', color: '#333' }
        : isRollup
          ? { fontStyle: 'italic', color: '#555' }
          : (r.memo ? { fontStyle: 'italic', color: '#666' } : undefined);
      const cls = ['indent'];
      if (isZeroSuppressed) cls.push('cf-zero-suppressed');
      return (
        <tr key={i} className={cls.join(' ')} style={style}>
          <td style={{ paddingLeft: (r.indent || 0) * 8 }}>{normalizeLeafLabel(r.label)}</td>
          <td>{r.value == null ? '' : fmt(r.value)}</td>
        </tr>
      );
    };
    return (
    <div className="page">
      <div className="header">
        <div className="company">{client?.company || 'Company Name'}</div>
      </div>
      <div className="statement-content">
        <div className="statement-title">{getStatementTitle('cf', framework, frameworkOtherLabel)}</div>
        <div className="statement-period">{`${cfPeriodLabel} — Indirect Method`}</div>

        {!cfData.hasPrior && (
          <div style={{
            margin: '6px 0 12px', padding: '6px 10px',
            border: '1px solid #FDBA74', background: '#FFF7ED',
            color: '#7C2D12', fontSize: 9, borderRadius: 4
          }}>
            <strong>DRAFT — Prior-period data missing.</strong>
            Working-capital deltas require a prior-period Trial Balance.
            Connect/sync QuickBooks to populate the prior year.
          </div>
        )}

        <table>
          <thead><tr><th>DESCRIPTION</th><th>AMOUNT</th></tr></thead>
          <tbody>
            <tr className="section-header">
              <td>CASH FLOWS FROM OPERATING ACTIVITIES</td><td></td>
            </tr>
            {cfData.sections.operating.rows.map(renderRow)}
            <tr className="subtotal">
              <td>Net Cash from Operating Activities</td>
              <td>{fmt(cfData.sections.operating.subtotal)}</td>
            </tr>

            <tr className="section-header">
              <td>CASH FLOWS FROM INVESTING ACTIVITIES</td><td></td>
            </tr>
            {cfData.sections.investing.rows.map(renderRow)}
            <tr className="subtotal">
              <td>Net Cash from Investing Activities</td>
              <td>{fmt(cfData.sections.investing.subtotal)}</td>
            </tr>

            <tr className="section-header">
              <td>CASH FLOWS FROM FINANCING ACTIVITIES</td><td></td>
            </tr>
            {cfData.sections.financing.rows.map(renderRow)}
            <tr className="subtotal">
              <td>Net Cash from Financing Activities</td>
              <td>{fmt(cfData.sections.financing.subtotal)}</td>
            </tr>

            <tr className="subtotal" data-testid="cf-net-change">
              <td>NET CHANGE IN CASH</td>
              <td>{fmt(cfData.netChange)}</td>
            </tr>
            <tr className="indent">
              <td>Beginning Cash Balance</td>
              <td>{fmt(cfData.beginCash)}</td>
            </tr>
            <tr className="grand-total" data-testid="cf-ending-cash">
              <td>ENDING CASH BALANCE</td>
              <td>{fmt(cfData.endCash)}</td>
            </tr>

            {/* Supplemental disclosures (ASC 230) — required separately
                from the body of the CF. Never thresholded, never rolled up. */}
            {(cfData.supplemental?.interestPaid || cfData.supplemental?.taxesPaid) ? (
              <>
                <tr><td colSpan={2} style={{ height: 8 }}></td></tr>
                <tr className="section-header">
                  <td>SUPPLEMENTAL DISCLOSURES OF CASH FLOW INFORMATION</td><td></td>
                </tr>
                {cfData.supplemental.interestPaid ? (
                  <tr className="indent" data-testid="cf-supp-interest">
                    <td>Cash paid for interest</td>
                    <td>{fmt(cfData.supplemental.interestPaid)}</td>
                  </tr>
                ) : null}
                {cfData.supplemental.taxesPaid ? (
                  <tr className="indent" data-testid="cf-supp-taxes">
                    <td>Cash paid for income taxes</td>
                    <td>{fmt(cfData.supplemental.taxesPaid)}</td>
                  </tr>
                ) : null}
              </>
            ) : null}
          </tbody>
        </table>
      </div>
      {footerText && <div className="footer">{footerText}</div>}
    </div>
    );
  };

  // BALANCE SHEET WITH PRIOR YEAR COMPARISON
  // BALANCE SHEET — PRIOR YEAR COMPARISON. Canonical, mapping-driven, uses real
  // QBO prior-period Trial Balance. The synthetic pyFactor multiplier is gone.
  const renderBalanceSheetPY = () => {
    const { sections } = bsData;
    const { reportingYear, priorYear, asOfLabel } = defaultPeriodLabels(qboMeta);

    // Variance helper — returns the percent string. Em-dash when prior is 0,
    // never NaN, never a hardcoded value.
    const pctChg = (curr, prior) => {
      const p = varPct(curr, prior);
      return p === null ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1)}%`;
    };
    const chgColor = (curr, prior, type) => {
      const diff = curr - prior;
      if (Math.abs(diff) < 1) return undefined;
      // For assets/equity-on-asset-side: positive growth = good (green)
      // For liabilities: positive growth = bad (red)
      if (type === 'liability') return diff > 0 ? '#C8322B' : '#1F9D57';
      return diff > 0 ? '#1F9D57' : '#C8322B';
    };

    const Row = ({ label, current, prior, type, indent = 1, bold, total, grand }) => {
      const diff = current - prior;
      const className = grand ? 'grand-total' : total ? 'total' : bold ? 'subtotal' : 'indent';
      return (
        <tr className={className}>
          <td style={{ paddingLeft: indent * 6 }}>{label}</td>
          <td>{fmt(current)}</td>
          <td>{fmt(prior)}</td>
          <td style={{ color: chgColor(current, prior, type) }}>{fmt(diff)}</td>
          <td style={{ color: chgColor(current, prior, type) }}>{pctChg(current, prior)}</td>
        </tr>
      );
    };
    const renderLines = (sec, type) => sec.lines.map((l, i) => (
      <Row
        key={`${sec.key}-${i}`}
        label={normalizeLeafLabel(l.label)}
        current={l.current}
        prior={l.prior}
        type={type}
        indent={2}
      />
    ));

    // Walk structured items (group + leaf) across all 5 columns. Group renders
    // header (bold, no value) + indented leaves + group subtotal — consistent
    // with the single-column BS. Always emit the subtotal row, even for a
    // single-child group, so layout stays stable as accounts are added.
    const renderItemsCmp = (items, type, baseIndent = 2) => {
      if (!items || items.length === 0) return null;
      return items.map((it, idx) => {
        if (it.kind === 'group') {
          return (
            <React.Fragment key={`g-${idx}-${it.label}`}>
              {/* Group header — bold black, no value cells (label only) */}
              <tr className="subtotal">
                <td style={{ paddingLeft: baseIndent * 6 }}>{it.label}</td>
                <td></td><td></td><td></td><td></td>
              </tr>
              {it.leaves.map((l, li) => (
                <Row
                  key={`gl-${idx}-${li}`}
                  label={normalizeLeafLabel(l.label)}
                  current={l.current}
                  prior={l.prior}
                  type={type}
                  indent={baseIndent + 1}
                />
              ))}
              <Row
                label={`Total ${it.label}`}
                current={it.subtotal}
                prior={it.subtotal_prior}
                type={type}
                indent={baseIndent}
                bold
              />
            </React.Fragment>
          );
        }
        return (
          <Row
            key={`leaf-${idx}`}
            label={it.label}
            current={it.current}
            prior={it.prior}
            type={type}
            indent={baseIndent}
          />
        );
      });
    };

    // Sum prior + current for each top-level section
    const py = {
      cash: sections.cash.total_prior,
      assetsCur: sections.assetsCur.total_prior,
      assetsFix: sections.assetsFix.total_prior,
      assetsOther: sections.assetsOther.total_prior,
      liabCur: sections.liabCur.total_prior,
      debtShort: sections.debtShort.total_prior,
      liabLong: sections.liabLong.total_prior,
      equity: sections.equity.total_prior,
    };
    // PY grand totals are sourced from the canonical bsData.prior so the
    // BS PY Comparison page foots exactly. Per-section subtotals (e.g.
    // py.liabCur) use largest-remainder rounded values for each section and
    // their sum can differ from the grand total by sub-cent rounding — that
    // sub-cent difference is the exact bug that caused V7's $1 L+E
    // imbalance on the printed comparison page.
    const totalAssetsPY = bsData.prior.totalAssets;
    const totalCAPY     = py.cash + py.assetsCur;
    const totalLiabPY   = bsData.prior.totalLiab;
    const niPrior       = bsData.prior.netIncome;
    const totalEquityPY = bsData.prior.totalEquity;
    const totalLEPY     = bsData.prior.totalLE;

    return (
      <div className="page">
        <div className="header">
          <div className="company">{client?.company || 'Company Name'}</div>
        </div>
        <div className="statement-content">
          <div className="statement-title">{getStatementTitle('bs_py', framework, frameworkOtherLabel)}</div>
          <div className="statement-period">{asOfLabel}</div>

          {/* Single continuous BS PY comparison table. Assets and
              Liabilities + Equity share the same `<table>` so the four
              numeric columns (current year, prior year, change, % chg)
              line up exactly: a vertical ruler dropped from TOTAL ASSETS
              lands on the dollar amount of TOTAL LIABILITIES AND
              MEMBERS' EQUITY. Single thead, single set of column
              widths. The L+E side transition is marked with a
              major-section-header row (same row structure as the other
              sub-section headers, plus a thin top rule + extra
              breathing room via the .major-section-header modifier). */}
          <table className="py-comparison bs-py-table">
            <thead>
              <tr>
                <th style={{ width: '36%' }}>ASSETS</th>
                <th style={{ width: '16%' }}>{reportingYear}</th>
                <th style={{ width: '16%' }}>{priorYear}</th>
                <th style={{ width: '16%' }}>CHANGE</th>
                <th style={{ width: '16%' }}>% CHG</th>
              </tr>
            </thead>
            <tbody>
              <tr className="section-header"><td>Current Assets</td><td></td><td></td><td></td><td></td></tr>
              {renderItemsCmp(sections.assetsCur.items, 'asset')}
              <Row label="Total Current Assets" current={sections.assetsCur.totalCombined} prior={sections.assetsCur.totalCombined_prior} type="asset" bold />
              {sections.assetsFix.items.length > 0 && (
                <>
                  <tr className="section-header"><td>Property & Equipment</td><td></td><td></td><td></td><td></td></tr>
                  {renderItemsCmp(sections.assetsFix.items, 'asset')}
                  <Row label="Total Property & Equipment" current={sections.assetsFix.total} prior={py.assetsFix} type="asset" bold />
                </>
              )}
              {sections.assetsOther.items.length > 0 && (
                <>
                  <tr className="section-header"><td>Other Assets</td><td></td><td></td><td></td><td></td></tr>
                  {renderItemsCmp(sections.assetsOther.items, 'asset')}
                  <Row label="Total Other Assets" current={sections.assetsOther.total} prior={py.assetsOther} type="asset" bold />
                </>
              )}
              <Row label="TOTAL ASSETS" current={totalAssets} prior={totalAssetsPY} type="asset" total />

              {/* Major-section transition row */}
              <tr className="section-header major-section-header">
                <td>LIABILITIES AND {equityLabels.equity.toUpperCase()}</td>
                <td></td><td></td><td></td><td></td>
              </tr>

              {sections.liabCur.items.length > 0 && (
                <>
                  <tr className="section-header"><td>Current Liabilities</td><td></td><td></td><td></td><td></td></tr>
                  {renderItemsCmp(sections.liabCur.items, 'liability')}
                  <Row label="Total Current Liabilities" current={sections.liabCur.total} prior={py.liabCur} type="liability" bold />
                </>
              )}
              {sections.debtShort.items.length > 0 && (
                <>
                  <tr className="section-header"><td>Short-Term Debt</td><td></td><td></td><td></td><td></td></tr>
                  {renderItemsCmp(sections.debtShort.items, 'liability')}
                  <Row label="Total Short-Term Debt" current={sections.debtShort.total} prior={py.debtShort} type="liability" bold />
                </>
              )}
              {sections.liabLong.items.length > 0 && (
                <>
                  <tr className="section-header"><td>Long-Term Liabilities</td><td></td><td></td><td></td><td></td></tr>
                  {renderItemsCmp(sections.liabLong.items, 'liability')}
                  <Row label="Total Long-Term Liabilities" current={sections.liabLong.total} prior={py.liabLong} type="liability" bold />
                </>
              )}
              <Row label="TOTAL LIABILITIES" current={totalLiab} prior={totalLiabPY} type="liability" bold />

              <tr className="section-header"><td>{equityLabels.equity}</td><td></td><td></td><td></td><td></td></tr>
              {renderItemsCmp(sections.equity.items, 'asset')}
              <Row label="Current Year Earnings" current={netIncome} prior={niPrior} type="asset" indent={2} />
              <Row label={`Total ${equityLabels.equity}`} current={totalEquity} prior={totalEquityPY} type="asset" bold />
              <Row label={`TOTAL LIABILITIES AND ${equityLabels.equity.toUpperCase()}`} current={totalLE} prior={totalLEPY} type="asset" grand />
            </tbody>
          </table>
        </div>
        {footerText && <div className="footer">{footerText}</div>}
      </div>
    );
  };

  // STATEMENT OF INCOME — PRIOR YEAR COMPARISON. Canonical, mapping-driven,
  // uses real QBO prior-period TB. The pyFactor and hardcoded +17.6% are gone.
  const renderIncomeStatementPY = () => {
    const { sections, totals, prior } = isData;
    const { reportingYear, priorYear, dualPeriodLabel } = defaultPeriodLabels(qboMeta);

    const pctChg = (curr, p) => {
      const r = varPct(curr, p);
      return r === null ? '—' : `${r > 0 ? '+' : ''}${r.toFixed(1)}%`;
    };
    const chgColor = (curr, p, kind) => {
      const diff = curr - p;
      if (Math.abs(diff) < 1) return undefined;
      // income kind: growth is good; expense kind: growth is bad
      if (kind === 'expense') return diff > 0 ? '#C8322B' : '#1F9D57';
      return diff > 0 ? '#1F9D57' : '#C8322B';
    };

    const Row = ({ label, current, prior: priorVal, kind, bold, total, grand, extraClass }) => {
      const diff = current - (priorVal || 0);
      const baseClass = grand ? 'grand-total' : total ? 'total' : bold ? 'subtotal' : 'indent';
      const className = extraClass ? `${baseClass} ${extraClass}` : baseClass;
      return (
        <tr className={className}>
          <td>{label}</td>
          <td>{fmt(current)}</td>
          <td>{fmt(priorVal || 0)}</td>
          <td style={{ color: chgColor(current, priorVal || 0, kind) }}>{fmt(diff)}</td>
          <td style={{ color: chgColor(current, priorVal || 0, kind) }}>{pctChg(current, priorVal)}</td>
        </tr>
      );
    };
    const renderLines = (sec, kind, opts = {}) => sec.lines.map((l, i) => (
      <Row
        key={`${sec.key}-${i}`}
        label={normalizeLeafLabel(l.label)}
        current={opts.negate ? -l.current : l.current}
        prior={opts.negate ? -(l.prior || 0) : (l.prior || 0)}
        kind={kind}
      />
    ));

    const hasOtherInc = sections.otherIncome.lines.length > 0;
    const hasOtherExp = sections.otherExpense.lines.length > 0;

    return (
      <div className="page">
        <div className="header">
          <div className="company">{client?.company || 'Company Name'}</div>
        </div>
        <div className="statement-content">
          <div className="statement-title">{getStatementTitle('pl_py', framework, frameworkOtherLabel)}</div>
          <div className="statement-period">{dualPeriodLabel}</div>

          <table className="py-comparison">
            <thead>
              <tr>
                <th>DESCRIPTION</th>
                <th>{reportingYear}</th>
                <th>{priorYear}</th>
                <th>CHANGE</th>
                <th>% CHG</th>
              </tr>
            </thead>
            <tbody>
              <tr className="section-header"><td>REVENUE</td><td></td><td></td><td></td><td></td></tr>
              {renderLines(sections.income, 'income')}
              <Row label="Total Revenue" current={totals.totalRevenue} prior={prior.totalRevenue} kind="income" bold />

              {sections.cogs.lines.length > 0 && (
                <>
                  <tr className="section-header"><td>COST OF REVENUE</td><td></td><td></td><td></td><td></td></tr>
                  {renderLines(sections.cogs, 'expense')}
                  <Row label="Total Cost of Revenue" current={totals.totalCOGS} prior={prior.totalCOGS} kind="expense" bold />
                </>
              )}
              <Row label="GROSS PROFIT" current={totals.grossProfit} prior={prior.grossProfit} kind="income" bold extraClass="is-gross-profit" />

              <tr className="section-header"><td>OPERATING EXPENSES</td><td></td><td></td><td></td><td></td></tr>
              {renderLines(sections.opex, 'expense')}
              <Row label="Total Operating Expenses" current={totals.totalOpEx} prior={prior.totalOpEx} kind="expense" bold />

              <Row label="INCOME FROM OPERATIONS" current={totals.ebit} prior={prior.ebit} kind="income" bold extraClass="is-income-from-ops" />

              {(hasOtherInc || hasOtherExp) && (
                <>
                  <tr className="section-header"><td>OTHER INCOME / (EXPENSE)</td><td></td><td></td><td></td><td></td></tr>
                  {renderLines(sections.otherIncome, 'income')}
                  {renderLines(sections.otherExpense, 'expense', { negate: true })}
                </>
              )}

              <Row label="NET INCOME" current={totals.netIncome} prior={prior.netIncome} kind="income" grand />
            </tbody>
          </table>
        </div>
        {footerText && <div className="footer">{footerText}</div>}
      </div>
    );
  };

  // T12 STATEMENT OF INCOME (Trailing 12 Months) - Full Income Statement Detail
  // FORMAT CONSISTENCY NOTE (2026-05-21): T12 deliberately uses
  // abbreviated K/M figures (via formatCurrency(v, true)) — BS / IS / CF
  // use full figures. The rule: with 14 columns on a single landscape
  // page (label + 12 months + T12 total), full figures overflow column
  // widths and cause horizontal clipping. Both the monthly cells AND
  // the T12 Total column share the same abbreviation logic so no row
  // mixes units. Per the user's directive (Part B.3, 2026-05-21):
  // "If T12 stays abbreviated for column-width reasons, document the
  // rule and ensure row totals use the same abbreviation logic as
  // monthly columns (no mixed-unit rows)."
  //
  // PAGINATION NOTE (2026-05-21, Part B.2 of T12 regression fix):
  // We explicitly chunk the row stream into landscape pages here in
  // JS rather than relying on the browser's natural page break. The
  // chunking guarantees:
  //   • Each page renders its own company masthead + statement title
  //     ("Trailing 12 Months Statement of Income (continued)" on the
  //     2nd+ pages — required by the user's acceptance criteria).
  //   • Net Income + Net Margin are atomic and land on the FINAL page.
  //   • Section headers (REVENUE, OPERATING EXPENSES, etc.) never
  //     orphan at the bottom of a page — if a section header would land
  //     in the last slot, it gets pushed to the next page.
  const renderT12Income = () => {
    const ttmMonths = ttmData?.months || [];
    const monthLabels = ttmMonths.map(m => `${m.month} ${String(m.year).slice(-2)}`);
    // V18.1 — compute the in-period month indices so the T12's
    // NI columns sum to the IS YTD NI on the rendered face. The
    // gate (`t12_ni_ties_to_is_ytd`) compares unrounded; this is
    // the display-rounding step that keeps the printed sum honest.
    const psDate = qboMeta?.period_start ? new Date(qboMeta.period_start + 'T12:00:00') : null;
    const peDate = qboMeta?.period_end   ? new Date(qboMeta.period_end   + 'T12:00:00') : null;
    const inPeriodIndices = [];
    if (psDate && peDate && ttmData?.months) {
      ttmData.months.forEach((m, i) => {
        const mStart = m.start ? new Date(m.start + 'T12:00:00') : null;
        const mEnd   = m.end   ? new Date(m.end   + 'T12:00:00') : null;
        if (mStart && mEnd && mStart >= psDate && mEnd <= peDate) inPeriodIndices.push(i);
      });
    }
    const t12 = consolidatedReports?.t12
      ? consolidatedReports.t12
      : (ttmData?.detail_lines_monthly
          ? buildT12(accounts, mapping, ttmData, monthLabels, { inPeriodIndices })
          : buildT12(ttmData?.accounts_monthly || [], mapping, monthLabels));
    const periodLabel = ttmMonths.length > 0
      ? `Trailing twelve months ${ttmMonths[0].month} ${ttmMonths[0].year} – ${ttmMonths[ttmMonths.length-1].month} ${ttmMonths[ttmMonths.length-1].year}`
      : `Trailing twelve months ended ${defaultPeriodLabels(qboMeta).asOfLabel.replace(/^As of /, '')}`;

    // V19 v4 (2026-06-12) — $-stripping rule for T12 interior body rows.
    // User mandate (3rd rejection of estimation-based column-width fixes):
    //   • Render `$` ONLY on the very first body row of each section AND
    //     on subtotal_total / margin (i.e. bold) rows.
    //   • Strip `$` from every other interior leaf / group_subtotal row.
    // This narrows the worst-case bold value text width (now most rows
    // are non-bold without $) and frees layout slack for the colgroup
    // geometry below.
    const fmtCellNoDollar = (v) => {
      if (v === undefined || v === null) return '';
      if (Math.abs(v) < 0.005) return '—';
      const raw = formatCurrency(v, false);
      // Strip leading '$' for positives, '($' opening for negatives.
      // formatCurrency emits either "$X" or "($X)" — drop the $ glyph
      // and rely on the comma-separated digits + parens for accounting
      // semantics. Tabular-nums alignment is preserved.
      return raw.replace(/\$/g, '');
    };
    const fmtCell = (v) => v === undefined || v === null
      ? ''
      : (Math.abs(v) < 0.005 ? '—' : formatCurrency(v, false));
    const fmtMargin = (v) => (v === null || v === undefined) ? '—' : `${v.toFixed(1)}%`;

    if (!consolidatedReports?.t12 && (!ttmData?.accounts_monthly || ttmData.accounts_monthly.length === 0)) {
      return (
        <div className="page-landscape">
          <div className="ls-inner">
            <div className="header"><div className="company">{client?.company || 'Company Name'}</div></div>
            <div className="statement-content">
              <div className="statement-title">{getStatementTitle('t12', framework, frameworkOtherLabel)}</div>
              <div className="statement-period">{periodLabel}</div>
              <div style={{ marginTop: 24, padding: 16, background: '#FEF3C7', borderRadius: 8, fontSize: 11, color: '#92400E' }}>
                Trailing-12-month per-account data is not available. Connect/sync QuickBooks Online for this client and retry.
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 1. Flatten t12.sections into a linear row stream of renderable rows.
    //    Each entry carries its own kind so renderTableRow can decide layout.
    //    Section headers are suppressed when the section has zero
    //    rows AND is not itself a subtotal/margin pseudo-section, so
    //    e.g. SMJ (a service firm with no COGS) doesn't get a bare
    //    "COST OF GOODS SOLD" header floating above the operating
    //    expenses section.
    const allRows = [];
    t12.sections.forEach((sec) => {
      const isPseudo = sec.isSubtotal || sec.isMargin;
      const hasContentRows = (sec.rows || []).length > 0;
      if (!isPseudo && hasContentRows) {
        allRows.push({ kind: 'section_header', label: sec.label });
      }
      let prevKind = null;
      let firstBodyRowEmitted = false;
      (sec.rows || []).forEach((row) => {
        // Suppress zero-only leaf rows so the table fits on one
        // landscape page without clipping Net Income / Net Margin.
        // Section headers, subtotals, and group rows always render.
        if (row.kind === 'leaf') {
          const monthsZero = (row.months || []).every(v => Math.round(Number(v) || 0) === 0);
          const totalZero  = Math.round(Number(row.total) || 0) === 0;
          if (monthsZero && totalZero) return;
        }
        // V19 v4 — flag the first row of the section that actually
        // emits monetary values (leaf or group_subtotal). The first
        // such row renders WITH `$`; subsequent rows in the same
        // section drop the `$` glyph.
        const isValueRow = (row.kind === 'leaf' || row.kind === 'group_subtotal');
        const _isFirstInSection = isValueRow && !firstBodyRowEmitted;
        if (isValueRow) firstBodyRowEmitted = true;
        allRows.push({ ...row, _prevKind: prevKind, _isFirstInSection });
        prevKind = row.kind;
      });
      if (sec.isSubtotal) {
        allRows.push({
          kind: 'subtotal_total',
          label: sec.label,
          monthsTotal: sec.monthsTotal,
          total: sec.total,
          isNI: sec.label === 'Net Income',
        });
      }
      if (sec.isMargin) {
        allRows.push({
          kind: 'margin',
          label: sec.label,
          monthsTotal: sec.monthsTotal,
          total: sec.total,
        });
      }
    });

    // 2. Single-page T12 (no chunking). Per user direction (2026-05-22):
    //    "This should fit on one page landscaped." We render the entire
    //    canonical IS row stream as ONE chunk and let the rotated landscape
    //    canvas (9.5in × 7.5in) absorb it. At 6pt font with table-layout:
    //    fixed and an account-column width that constrains the 14-column
    //    layout to fit within 9.5in, all ~30-35 rows fit comfortably in
    //    the ~6.5in body height.
    //
    //    The defensive chunker (with row-per-page caps + section-header
    //    orphan protection + last-chunk rebalancing) is retained but
    //    bypassed by setting the cap above any realistic row count. If a
    //    client ever has so many lines that the T12 truly cannot fit on
    //    one page, the chunker will reactivate cleanly via the original
    //    code path — but the default is single-page.
    const chunks = [allRows];
    const nonEmptyChunks = chunks.filter(c => c.length > 0);
    if (nonEmptyChunks.length === 0) {
      // No data at all — fall through to no-data render below.
      return [];
    }

    const renderTableRow = (row, ri) => {
      if (row.kind === 'section_header') {
        return (
          <tr key={`r-${ri}`} className="section-header">
            <td>{row.label.toUpperCase()}</td>
            {t12.months.map((_, i) => <td key={i}></td>)}
            <td></td>
          </tr>
        );
      }
      if (row.kind === 'subtotal_total') {
        return (
          <tr key={`r-${ri}`} className={row.isNI ? 'grand-total' : 'subtotal'}>
            <td>{row.label}</td>
            {(row.monthsTotal || []).map((v, mi) => <td key={mi}>{fmtCell(v)}</td>)}
            <td>{fmtCell(row.total)}</td>
          </tr>
        );
      }
      if (row.kind === 'margin') {
        return (
          <tr key={`r-${ri}`} className="t12-net-margin" style={{ fontStyle: 'italic', color: '#666' }}>
            <td>{row.label}</td>
            {(row.monthsTotal || []).map((v, mi) => <td key={mi}>{fmtMargin(v)}</td>)}
            <td>{fmtMargin(row.total)}</td>
          </tr>
        );
      }
      // leaf / group_header / group_subtotal
      const indent = row.kind === 'leaf' ? 1 : 0;
      const indentExtra = (row.kind === 'leaf' && row._prevKind === 'group_header') ? 1 : 0;
      const className = (row.kind === 'group_header' || row.kind === 'group_subtotal') ? 'subtotal' : 'indent';
      // V19 v4 — $-stripping: the first body row of a section keeps its $
      // glyph; all subsequent interior body rows render without $ to free
      // column width. Bold subtotal/grand-total rows (handled in the
      // subtotal_total / margin branches above) always render with $.
      const cellFmt = row._isFirstInSection ? fmtCell : fmtCellNoDollar;
      return (
        <tr key={`r-${ri}`} className={className}>
          <td style={{ paddingLeft: 6 + (indent + indentExtra) * 6 }}>{row.label}</td>
          {(row.months || Array(t12.months.length).fill(0)).map((v, mi) => (
            <td key={mi}>{row.kind === 'group_header' ? '' : cellFmt(v)}</td>
          ))}
          <td>{row.kind === 'group_header' ? '' : cellFmt(row.total)}</td>
        </tr>
      );
    };

    return nonEmptyChunks.map((chunkRows, chunkIdx) => (
      <div
        key={`t12-page-${chunkIdx}`}
        className={`page-landscape${effectiveReportType === 'compiled' ? ' t12-compiled' : ''}`}
      >
        {/* .ls-inner is the rotated landscape canvas. The outer wrapper
            is a 7.5in × 10in portrait area; this inner block is 10in ×
            7.5in rotated -90deg to fill the same area sideways. See CSS
            comment above .page-landscape for why this approach replaced
            the @page-named-page-rule landscape implementation. */}
        <div className="ls-inner">
          <div className="header">
            <div className="company">{client?.company || 'Company Name'}</div>
          </div>
          <div className="statement-content">
            {/* Per Part A.8: for compiled engagements, the T12 is presented
                as Supplementary Information (not a primary statement under
                GAAP). The "Supplementary Information" badge renders on the
                first chunk only; the (continued) marker handles 2nd+ pages. */}
            {chunkIdx === 0 && effectiveReportType === 'compiled' && (
              <div style={{
                fontSize: 7, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: '#6B7280',
                textAlign: 'center', marginBottom: 1, lineHeight: 1.0,
              }}>
                Supplementary Information
              </div>
            )}
            <div className="statement-title">
              {getStatementTitle('t12', framework, frameworkOtherLabel)}{chunkIdx > 0 ? ' (continued)' : ''}
            </div>
            <div className="statement-period">{periodLabel}</div>
            {chunkIdx === 0 && effectiveReportType === 'compiled' && (
              <div style={{
                fontSize: 6.8, fontStyle: 'italic', color: '#4B5563',
                padding: '1px 8px', background: '#F9FAFB',
                borderLeft: '2px solid #0085CA',
                marginBottom: 2, lineHeight: 1.15,
              }}>
                Management has also presented the trailing twelve months statement of income, which is presented for purposes of additional analysis and is not a required part of the basic financial statements.
              </div>
            )}
            <table style={{
              fontSize: '8pt',
              lineHeight: 1.15,
              width: '100%',
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              borderSpacing: 0,
            }}>
              <colgroup>
                {/* V24 (2026-06-19) — column geometry verified in a
                    Playwright landscape harness against the firm's June-11
                    reference. Account column 1.65in (2-line wrap, no
                    truncation); 12 month columns 0.625in each; T12 TOTAL
                    column 0.88in, set off with a left rule. Sum = 1.65 +
                    12×0.625 + 0.88 = 10.03in ≈ letter-landscape usable
                    width (11in − 2×0.5in margins). */}
                <col style={{ width: '1.65in' }} />
                {t12.months.map((_, i) => <col key={i} style={{ width: '0.625in' }} />)}
                <col style={{ width: '0.88in' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>ACCOUNT</th>
                  {t12.months.map((m, i) => <th key={i}>{m}</th>)}
                  <th>T12 TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {chunkRows.map((row, ri) => renderTableRow(row, ri))}
              </tbody>
            </table>
          </div>
          {/* T12 running-footer SOURCE (out-of-flow marker). The
              server-side renderer reads `[data-footer-source]` to
              populate Chrome's running footer template. Using
              position:absolute + display:none-like dimensions keeps
              this from affecting the .ls-inner table's available
              height (the previous .footer-as-sibling approach baked
              the legend's height into the table's initial layout
              even after visibility:hidden, clipping Net Income/Net
              Margin on prepared T12). */}
          {footerText && (
            <span
              data-footer-source="1"
              style={{ position: 'absolute', left: -99999, top: 0, width: 0, height: 0, overflow: 'hidden' }}
            >
              {footerText}
            </span>
          )}
        </div>
      </div>
    ));
  };

  // Render statement by ID
  const renderStatement = (stmtId) => {
    switch(stmtId) {
      case 'bs': return renderBalanceSheet();
      case 'pl': return renderIncomeStatement();
      case 'cf': return renderCashFlow();
      case 'bs_py': return renderBalanceSheetPY();
      case 'pl_py': return renderIncomeStatementPY();
      case 't12': return renderT12Income();
      default: return null;
    }
  };

  // Checkbox component
  const StatementCheckbox = ({ id, checked, onChange, label, description }) => (
    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 6,
      background: checked ? COLORS.primaryLight : COLORS.subtle,
      cursor: 'pointer',
      marginBottom: 6,
    }}>
      <div style={{
        width: 18,
        height: 18,
        borderRadius: 4,
        border: `2px solid ${checked ? COLORS.primary : COLORS.border}`,
        background: checked ? COLORS.primary : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
      }}>
        {checked && <Check size={12} color="#fff" />}
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display: 'none' }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: checked ? COLORS.primary : COLORS.dark }}>{label}</div>
        <div style={{ fontSize: 11, color: COLORS.muted }}>{description}</div>
      </div>
    </label>
  );

  // ── HEADLESS MODE (V16 Pass D) ─────────────────────────────────────
  // When invoked by the /print-preview route (consumed by the Playwright
  // subprocess of /api/pdf/generate), bypass the modal UI entirely and
  // render the statement package directly into the document. The
  // PrintPreviewPage host sets the body class .pdf-server.section-* so
  // the CSS overrides activate (T12 native landscape, fonts bumped, etc.).
  if (headless) {
    const stmts = getStatementsToPrint();
    // Section semantics (2026-02-09):
    //   - 'cover'      → cover page (+ compilation report for compiled).
    //                    NO running footer (each has its own footer block;
    //                    "see accountant's compilation report" never
    //                    stamps on the report itself or the cover).
    //   - 'portrait'   → basis-of-accounting + BS + IS + CF + BS-PY + IS-PY.
    //                    Running footer active.
    //   - 'landscape'  → T12. Running footer active, rotates with content.
    //   - 'all'        → legacy single-pass (debugging only).
    const sectionFiltered = section === 'portrait'
      ? stmts.filter((s) => s !== 't12')
      : section === 'landscape'
      ? stmts.filter((s) => s === 't12')
      : section === 'cover'
      ? []  // cover/compilation rendered via include flags below; no statements
      : stmts;
    const includeCover = section === 'cover' || section === 'all';
    const includeCompilation = (section === 'cover' || section === 'all')
      && effectiveReportType === 'compiled' && !isClient;
    const includeDescBasis = (section === 'portrait' || section === 'all') && shouldRenderDescBasis;
    // CRITICAL: when the section is landscape, override the default
    // `@page { size: letter }` rule with `@page { size: letter landscape }`.
    // Empirically (V16 dev), Chrome's page.pdf() prefers the CSS @page
    // size declaration over the {width,height,landscape} arguments when
    // @page is present in the document — so without this override the
    // T12 page renders portrait regardless of Playwright args.
    const pageSizeCss = section === 'landscape'
      ? '@page { size: letter landscape; margin: 0.4in 0.5in; }'
      : '';
    return (
      <div data-testid="print-headless-root">
        <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
        {pageSizeCss && <style dangerouslySetInnerHTML={{ __html: pageSizeCss }} />}
        {includeCover && renderCoverPage()}
        {includeCompilation && renderCompilationReport()}
        {includeDescBasis && renderDescriptionOfBasisPage()}
        {sectionFiltered.map((stmtId) => (
          <React.Fragment key={stmtId}>{renderStatement(stmtId)}</React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div 
      className="smj-print-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="smj-print-modal-shell"
        style={{
          background: '#fff',
          borderRadius: 12,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <div className="smj-print-modal-toolbar" style={{ 
          padding: '20px 24px', 
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: COLORS.dark, margin: 0 }}>Print Financial Statements</h3>
            <p style={{ fontSize: 12, color: COLORS.muted, margin: '4px 0 0' }}>{client?.company}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.muted }}>
            <X size={20} />
          </button>
        </div>

        {/* Options */}
        <div className="smj-print-modal-body" style={{ padding: 24 }}>
          {/* Period Selection */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Time Period
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setPeriodId(opt.id)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 6,
                    border: `2px solid ${periodId === opt.id ? COLORS.primary : COLORS.border}`,
                    background: periodId === opt.id ? '#E0F7FA' : '#fff',
                    color: periodId === opt.id ? COLORS.primary : COLORS.dark,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {periodId === 'custom' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }}
                />
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }}
                />
              </div>
            )}
            {/* V20.1 — Period refetch state surfacing. */}
            {periodLoading && (
              <div data-testid="period-loading" style={{ marginTop: 10, padding: '8px 12px', background: '#F0F9FF',
                                  border: `1px solid ${COLORS.primary}`, borderRadius: 6,
                                  fontSize: 12, color: COLORS.primary }}>
                Loading QBO data for selected period…
              </div>
            )}
            {periodError && !periodLoading && (
              <div data-testid="period-error" style={{ marginTop: 10, padding: '8px 12px', background: '#FEF2F2',
                                  border: `1px solid #C8322B`, borderRadius: 6,
                                  fontSize: 12, color: '#7F1D1D' }}>
                Could not load period data: {periodError}
              </div>
            )}
            {overrideQboMeta?.period_end && !periodLoading && !periodError && periodId !== 'prior_month' && (
              <div data-testid="period-loaded" style={{ marginTop: 10, padding: '6px 12px', fontSize: 11, color: COLORS.muted }}>
                Showing QBO data through {overrideQboMeta.period_end}
              </div>
            )}
            {/* V20.2 — Mid-year / non-FY-start advisory. See the matching
                advisory in StaffDashboard. CF identity variance can occur
                for Last Fiscal Quarter (Q2-Q4) or custom ranges starting
                after Jan 1 because QBO's TB returns YTD-from-FY-start
                P&L. Surface this in the Print modal too so staff don't
                ship a package that won't tie. */}
            {/* V21 (Day 3 cutover) — orange period-warning banner deleted.
                V21 builder makes the CF identity hold for any period
                shape; see /app/memory/V21_DAY2_DIVERGENCE_LOG.md for
                the 24/24 acceptance matrix at exact $0.00 identity. */}
          </div>

          {/* Package Options */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              What to Print
            </label>
            {PACKAGE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                data-testid={`package-option-${opt.id}`}
                onClick={() => setPackageOption(opt.id)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: 8,
                  borderRadius: 8,
                  border: `2px solid ${packageOption === opt.id ? COLORS.primary : COLORS.border}`,
                  background: packageOption === opt.id ? '#E0F7FA' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: packageOption === opt.id ? COLORS.primary : COLORS.dark }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{opt.description}</div>
              </button>
            ))}
            
            {/* Custom Package Checkboxes */}
            {packageOption === 'custom' && (
              <div style={{ marginTop: 12, padding: 12, background: COLORS.subtle, borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 10, textTransform: 'uppercase' }}>
                  Select Statements to Include
                </div>
                {STATEMENT_OPTIONS.map(stmt => (
                  <StatementCheckbox
                    key={stmt.id}
                    id={stmt.id}
                    checked={selectedStatements.includes(stmt.id)}
                    onChange={() => toggleStatement(stmt.id)}
                    label={stmt.label}
                    description={stmt.description}
                  />
                ))}
              </div>
            )}

            {/* Cash Flow zero-line suppression toggle (Day-2 presentation
                pass — item #1). Defaults to OFF (zero lines suppressed,
                cleaner deliverable). When checked, every CF row renders
                regardless of value — useful for completeness verification. */}
            {!isClient && (
              <div style={{ marginTop: 12 }}>
                <StatementCheckbox
                  id="show-zero-lines"
                  checked={showZeroLines}
                  onChange={() => setShowZeroLines(v => !v)}
                  label="Show zero lines on Statement of Cash Flows"
                  description="Default: hide rows with $0 activity. Check to render all CF lines for completeness review."
                />
              </div>
            )}
          </div>

          {/* Report Type - Only show for staff, not clients */}
          {!isClient && (
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Report Type
            </label>
            {REPORT_TYPES.map(opt => (
              <button
                key={opt.id}
                onClick={() => setReportType(opt.id)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: 8,
                  borderRadius: 8,
                  border: `2px solid ${reportType === opt.id ? COLORS.primary : COLORS.border}`,
                  background: reportType === opt.id ? '#E0F7FA' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: reportType === opt.id ? COLORS.primary : COLORS.dark }}>{opt.label}</div>
                <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 4, fontStyle: 'italic' }}>Footer: "{opt.footer}"</div>
              </button>
            ))}
          </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 24px 24px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 12 }}>
          <button
            onClick={pdfGenerating ? undefined : onClose}
            disabled={pdfGenerating}
            data-testid="print-modal-cancel-btn"
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: 8,
              border: `2px solid ${COLORS.border}`,
              background: '#fff',
              color: COLORS.dark,
              fontSize: 13,
              fontWeight: 600,
              cursor: pdfGenerating ? 'not-allowed' : 'pointer',
              opacity: pdfGenerating ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handlePrintClick}
            data-testid="print-modal-generate-btn"
            disabled={pdfGenerating}
            style={{
              flex: 2,
              padding: '12px',
              borderRadius: 8,
              border: 'none',
              background: COLORS.primary,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Printer size={16} /> Print {packageOption === 'current' ? 'Statement' : 'Financial Package'}
          </button>
        </div>
      </div>

      {/* Hidden print content */}
      <div ref={printRef} style={{ display: 'none' }}>
        {packageOption === 'current' ? (
          renderStatement(getStatementsToPrint()[0])
        ) : (
          <>
            {renderCoverPage()}
            {effectiveReportType === 'compiled' && !isClient && renderCompilationReport()}
            {shouldRenderDescBasis && renderDescriptionOfBasisPage()}
            {getStatementsToPrint().map(stmtId => (
              <React.Fragment key={stmtId}>
                {renderStatement(stmtId)}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      {/* V18 Step 2.5 — Unsaved-mapping-changes warning */}
      {unsavedWarning && (
        <div
          data-testid="unsaved-mapping-warning"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100, padding: 20,
          }}
        >
          <div style={{
            background: '#fff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 480,
            padding: 28,
            boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
              Unsaved mapping changes
            </div>
            <div style={{ fontSize: 13, color: COLORS.dark, lineHeight: 1.55, marginBottom: 22 }}>
              You have unsaved changes to the Chart of Accounts mapping.
              Save before printing, or the PDF will reflect the last saved
              version on the server.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={unsavedWarning.onSaveAndContinue}
                data-testid="unsaved-save-and-continue-btn"
                style={{
                  padding: '11px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: COLORS.primary,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save and continue
              </button>
              <button
                onClick={unsavedWarning.onContinueAnyway}
                data-testid="unsaved-continue-anyway-btn"
                style={{
                  padding: '11px 14px',
                  borderRadius: 8,
                  border: `2px solid ${COLORS.border}`,
                  background: '#fff',
                  color: COLORS.dark,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Continue anyway (use saved version)
              </button>
              <button
                onClick={unsavedWarning.onCancel}
                data-testid="unsaved-cancel-btn"
                style={{
                  padding: '11px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'transparent',
                  color: COLORS.muted,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
